import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { quotaAlert } from '@/lib/routeQuota';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';

import { 
  CalendarIcon, Plus, Clock, Users, GraduationCap, Presentation,
  Trash2, Edit, CheckCircle2, Calendar as CalendarLucide,
  UserPlus, Search, Mail, ArrowLeft, Save, BarChart3, DollarSign, Copy, Download,
  ShieldCheck, Upload, Award, Eye, Send, Video, RefreshCw, ExternalLink, FileText, X, Loader2, AlertTriangle,
} from 'lucide-react';
import { useCohortCalendarSync } from '@/hooks/useCohortCalendarSync';
import { supabase } from '@/integrations/supabase/client';
import { weekAnchorDate } from '@/lib/cohortWeeks';
import { OrganizationFilter } from '@/components/admin/OrganizationFilter';
import { useCohortManagement, type CohortRow, type ScheduleItemRow } from '@/hooks/useCohortManagement';
import { useAuth } from '@/contexts/AuthContext';
import { CohortProgressTab } from '@/components/admin/CohortProgressTab';
import { CohortCertificationTab } from '@/components/admin/CohortCertificationTab';
import { GeneratedCertificatesView } from '@/components/admin/GeneratedCertificatesView';
import { CohortResultsReportCard } from '@/components/admin/CohortResultsReportCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { useExportMetrics } from '@/hooks/useExportMetrics';
import * as XLSX from 'xlsx';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';
import { paginateQuery } from '@/lib/paginateSupabase';

// ── Helpers ──

// Parse a 'yyyy-MM-dd' string as a local date (avoid UTC shift)
const parseLocalDate = (s: string | null | undefined): Date | undefined => {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const isPastDate = (s: string | null | undefined) => !!s && s < todayISO();
// Una sesión es pasada en cuanto termina su hora de fin (hora Colombia). Las
// sesiones pasadas nunca se sincronizan con Google Calendar.
const sessionHasEnded = (i: { scheduled_date?: string | null; end_hour?: number | null; end_minute?: number | null }) => {
  if (!i.scheduled_date) return false;
  const p2 = (n: number) => String(n).padStart(2, '0');
  return new Date(`${i.scheduled_date}T${p2(i.end_hour ?? 23)}:${p2(i.end_minute ?? 59)}:00-05:00`).getTime() <= Date.now();
};

/** Semana en curso de la cohorte (según fecha de inicio) sobre el total del calendario. */
const getWeekLabel = (c: CohortRow): string | null => {
  if (c.is_perpetual || !c.start_date) return null;
  if (parseLocalDate(c.start_date)! > new Date()) return 'Aún no inicia';
  // Semanas contadas desde la fecha ancla (cohortes nuevas: inicio + 1 día).
  const start = parseLocalDate(weekAnchorDate(c.start_date, (c as any).first_session_after_start))!;
  const now = new Date();
  const diffWeeks = Math.floor((now.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
  const end = parseLocalDate(c.end_date);
  const total =
    c.max_week ??
    (end ? Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000))) : null);
  if (total && diffWeeks > total) return `Finalizada (${total} semanas)`;
  return total ? `Semana ${diffWeeks} de ${total}` : `Semana ${diffWeeks}`;
};

/** Qué falta del demoday una vez pasada su fecha (grabación / transcripción). */
const demoDayMissing = (c: CohortRow): string | null => {
  if (!c.demo_day_date || !isPastDate(c.demo_day_date)) return null;
  const missing: string[] = [];
  if (!(c.demo_day_recording_link ?? '').trim()) missing.push('sin grabación');
  if (!(c.demo_day_transcript_url ?? '').trim()) missing.push('sin transcripción');
  return missing.length ? missing.join(' y ') : null;
};

const WarnIcon = ({ text }: { text: string }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span onClick={e => e.stopPropagation()} className="inline-flex">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs whitespace-pre-line">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);



const hours = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`
}));

const minutes = [
  { value: 0, label: '00' },
  { value: 15, label: '15' },
  { value: 30, label: '30' },
  { value: 45, label: '45' },
];

const getStatusBadge = (status: string | null) => {
  switch (status) {
    case 'active':
      return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Activa</Badge>;
    case 'completed':
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Completada</Badge>;
    default:
      return <Badge variant="secondary">Borrador</Badge>;
  }
};

// ── Component ──

export default function CohortManagementPage() {
  const { currentRole, user } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  // Auditor: rol de SOLO LECTURA. Se ocultan todas las acciones de mutación
  // (crear/editar/eliminar cohortes, sync, asignaciones, importar, certificados…).
  const isAuditor = currentRole === 'auditor';
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const cm = useCohortManagement(selectedOrg);
  const calSync = useCohortCalendarSync();
  const queryClient = useQueryClient();
  const routeQuotaUsage = useQuery({
    queryKey: ['org-route-quota-usage'],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('org_route_quota_usage');
      if (error) throw error;
      return new Map<string, { used: number; contracted: number | null }>(
        (data ?? []).map((r: any) => [`${r.organization_id}|${r.program_id}`, { used: Number(r.used), contracted: r.contracted }]),
      );
    },
    staleTime: 60 * 1000,
  });
  const quotaWarning = (cohort: any): string | null => {
    if (!cohort.organization_id || cohort.is_perpetual) return null;
    const u = routeQuotaUsage.data?.get(`${cohort.organization_id}|${cohort.program_id}`);
    if (!u) return null;
    const a = quotaAlert(u.used, u.contracted);
    if (!a.exceeds) return null;
    const org = cohort.organization_name || 'La empresa';
    const n = cm.cohorts.filter((c: any) => c.organization_id === cohort.organization_id && c.program_id === cohort.program_id && !c.is_perpetual).length;
    return a.hasQuota
      ? `${org} tiene ${u.used} personas inscritas en esta ruta (en ${n} ${n === 1 ? 'cohorte' : 'cohortes'}), pero su cupo contratado es de ${u.contracted}. Excede en ${a.excess}. Ajusta el cupo de la ruta en Organizaciones o revisa las inscripciones.`
      : `${org} no tiene cupo contratado para esta ruta y tiene ${u.used} personas inscritas. Registra el cupo en Organizaciones.`;
  };
  const completedEnterpriseDiagnostics = useQuery({
    queryKey: ['enterprise-diagnostics', 'cohort-management-status', user?.id],
    enabled: isSuperAdmin && !!user,
    queryFn: async () => {
      const rows = await paginateQuery<{ organization_id: string }>((from, to) =>
        supabase.from('enterprise_diagnostics')
          .select('organization_id')
          .not('completed_at', 'is', null)
          .range(from, to),
      );
      return new Set(rows.map(row => row.organization_id));
    },
  });

  // Re-sincronizar el snapshot de currículo de la cohorte con el estado ACTUAL de la
  // ruta (empuja a propósito los cambios de contenido a una cohorte). Guardado por rol
  // en el RPC resync_cohort_curriculum (super_admin o admin de la org de la cohorte).
  const [resyncingCurriculum, setResyncingCurriculum] = useState(false);
  const handleResyncCurriculum = async (cohortId: string) => {
    setResyncingCurriculum(true);
    try {
      const { error } = await (supabase as any).rpc('resync_cohort_curriculum', { _cohort_id: cohortId });
      if (error) throw error;
      toast({ title: 'Currículo actualizado', description: 'El contenido obligatorio de la cohorte se re-sincronizó con la ruta actual.' });
      queryClient.invalidateQueries({ queryKey: ['cohort-progress', cohortId] });
      queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
      queryClient.invalidateQueries({ queryKey: ['cohort-certification'] });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setResyncingCurriculum(false);
    }
  };

  const { exportToExcel } = useExportMetrics();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCohortId, setEditingCohortId] = useState<string | null>(null);
  const [editTab, setEditTab] = useState('general');
  const [showCertificates, setShowCertificates] = useState(false);

  // Search & filter state for list view
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterModality, setFilterModality] = useState<string>('cohort');
  const [filterProgram, setFilterProgram] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterVisibility, setFilterVisibility] = useState<string>('all');
  const [filterEnrollment, setFilterEnrollment] = useState<string>('all');
  const [filterTimeframe, setFilterTimeframe] = useState<string>('all');

  // Create form state
  const [formName, setFormName] = useState('');
  const [formProgramId, setFormProgramId] = useState('');
  const [formOrgId, setFormOrgId] = useState('');
  const [formStartDate, setFormStartDate] = useState<Date | undefined>(undefined);
  const [formEndDate, setFormEndDate] = useState<Date | undefined>(undefined);
  const [formDemoDayDate, setFormDemoDayDate] = useState<Date | undefined>(undefined);
  const [formDemoDayStartHour, setFormDemoDayStartHour] = useState(9);
  const [formDemoDayEndHour, setFormDemoDayEndHour] = useState(13);
  const [formIsPublic, setFormIsPublic] = useState(false);
  const [formSessionPeriodicity, setFormSessionPeriodicity] = useState(7);
  const [formWeekDateOverrides, setFormWeekDateOverrides] = useState<Record<number, Date | null>>({});

  // Assign participants state
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [assigningCohortId, setAssigningCohortId] = useState<string | null>(null);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);
  const [participantSearch, setParticipantSearch] = useState('');

  // Assign mentors state
  const [isMentorDialogOpen, setIsMentorDialogOpen] = useState(false);
  const [assigningMentorCohortId, setAssigningMentorCohortId] = useState<string | null>(null);
  const [selectedMentorIds, setSelectedMentorIds] = useState<string[]>([]);
  const [mentorSearch, setMentorSearch] = useState('');

  // Assign auditors state (clon del patrón de mentores)
  const [isAuditorDialogOpen, setIsAuditorDialogOpen] = useState(false);
  const [assigningAuditorCohortId, setAssigningAuditorCohortId] = useState<string | null>(null);
  const [selectedAuditorIds, setSelectedAuditorIds] = useState<string[]>([]);
  const [auditorSearch, setAuditorSearch] = useState('');

  // Import participants state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Request cohort contract (admin de empresa)
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [requestProgramId, setRequestProgramId] = useState('');
  const [requestStartDate, setRequestStartDate] = useState<Date | undefined>(undefined);
  const [requestEndDate, setRequestEndDate] = useState<Date | undefined>(undefined);
  const [requestNotes, setRequestNotes] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);

  const handleSubmitRequest = async () => {
    if (!requestProgramId || !requestStartDate || !requestEndDate) {
      toast({ title: 'Completa todos los campos', variant: 'destructive' });
      return;
    }
    if (requestEndDate < requestStartDate) {
      toast({ title: 'La fecha fin debe ser posterior a la fecha inicio', variant: 'destructive' });
      return;
    }
    setRequestSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('request-cohort-contract', {
        body: {
          programId: requestProgramId,
          startDate: format(requestStartDate, 'yyyy-MM-dd'),
          endDate: format(requestEndDate, 'yyyy-MM-dd'),
          notes: requestNotes || null,
        },
      });
      if (error || (data as any)?.error) {
        throw new Error((data as any)?.error || error?.message || 'Error al enviar la solicitud');
      }
      toast({ title: 'Solicitud enviada', description: 'Te contactaremos pronto.' });
      setIsRequestDialogOpen(false);
      setRequestProgramId('');
      setRequestStartDate(undefined);
      setRequestEndDate(undefined);
      setRequestNotes('');
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setRequestSubmitting(false);
    }
  };


  // Editing cohort data
  const editingCohort = useMemo(() => {
    if (!editingCohortId) return null;
    return cm.filteredCohorts.find(c => c.id === editingCohortId) || cm.cohorts.find(c => c.id === editingCohortId) || null;
  }, [editingCohortId, cm.cohorts, cm.filteredCohorts]);

  // ── Edit-mode sub-queries ──
  const scheduleQuery = cm.useScheduleItems(editingCohortId);
  const participantsQuery = cm.useCohortParticipants(editingCohortId);
  const mentorsQuery = cm.useCohortMentors(editingCohortId);
  const auditorsQuery = cm.useCohortAuditors(editingCohortId);

  const cohortFinishedForSync = useMemo(() => {
    if (!editingCohort) return false;
    const today = new Date().toISOString().slice(0, 10);
    const status = (editingCohort as any).status as string | undefined;
    const endDate = (editingCohort as any).end_date as string | undefined;
    return (!!status && status !== 'active') || (!!endDate && endDate < today);
  }, [editingCohort]);

  // La ruta solo define si contempla mentorías; la cohorte crea las suyas.
  const editingProgramId = (editingCohort as any)?.program_id as string | undefined;
  const programMentorshipsFlagQuery = useQuery({
    queryKey: ['program-has-mentorships', editingProgramId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('has_mentorships')
        .eq('id', editingProgramId!)
        .maybeSingle();
      if (error) throw error;
      return (data as any)?.has_mentorships ?? false;
    },
    enabled: !!editingProgramId,
    staleTime: 5 * 60 * 1000,
  });
  const programHasMentorships = programMentorshipsFlagQuery.data === true;

  const assigningCohort = cm.cohorts.find(c => c.id === assigningCohortId);
  const assigningMentorCohort = cm.cohorts.find(c => c.id === assigningMentorCohortId);
  const assigningAuditorCohort = cm.cohorts.find(c => c.id === assigningAuditorCohortId);

  // ── Sugerencia de fechas para crear cohorte ──
  const programSessionsQuery = useQuery({
    queryKey: ['program-sessions-preview', formProgramId],
    queryFn: async () => {
      // Las mentorías ya no vienen de la ruta: cada cohorte crea las suyas.
      const wsRes = await supabase.from('program_workshops')
        .select('id, title, week_number, sort_order')
        .eq('program_id', formProgramId)
        .is('deleted_at', null)
        .order('sort_order', { ascending: true });
      if (wsRes.error) throw new Error('No se pudieron cargar las sesiones en vivo de la ruta.');
      type SessionEntry = { title: string; type: 'workshop' | 'mentorship' };
      const sessionsByWeek: Record<number, SessionEntry[]> = {};
      (wsRes.data || []).forEach(w => {
        if (typeof w.week_number !== 'number') return;
        if (!sessionsByWeek[w.week_number]) sessionsByWeek[w.week_number] = [];
        sessionsByWeek[w.week_number].push({ title: w.title || 'Sesión en vivo', type: 'workshop' });
      });
      const allWeeks = (wsRes.data || [])
        .map(w => w.week_number)
        .filter((w): w is number => typeof w === 'number');
      const uniqueWeeks = [...new Set(allWeeks)].sort((a, b) => a - b);
      return {
        workshopsCount: wsRes.data?.length || 0,
        totalSessions: wsRes.data?.length || 0,
        uniqueWeeks,
        maxWeek: uniqueWeeks.length > 0 ? Math.max(...uniqueWeeks) : 0,
        sessionsByWeek,
      };
    },
    enabled: !!formProgramId,
    staleTime: 5 * 60 * 1000,
  });

  // Detecta la ruta "Profesional Líder IA": sus mentorías se agendan luego,
  // no por periodicidad al crear la cohorte.
  const isLeaderProfessionalProgram = useMemo(() => {
    const program = cm.programs.find(p => p.id === formProgramId);
    if (!program) return false;
    if (program.slug === 'profesional-lider-ia') return true;
    const name = (program.name || '').toLowerCase().trim();
    return name === 'profesional líder ia' || name === 'profesional lider ia';
  }, [cm.programs, formProgramId]);

  // Fecha fin calculada automáticamente desde fecha inicio + periodicidad + semana máxima
  const formEndDateCalculated = useMemo(() => {
    if (!formStartDate || !programSessionsQuery.data?.maxWeek) return undefined;
    const d = new Date(formStartDate);
    // Última sesión = inicio + semana máxima × periodicidad.
    d.setDate(d.getDate() + programSessionsQuery.data.maxWeek * formSessionPeriodicity);
    return d;
  }, [formStartDate, formSessionPeriodicity, programSessionsQuery.data?.maxWeek]);

  // Sincronizar la fecha fin calculada con el estado
  useEffect(() => {
    if (formEndDateCalculated) setFormEndDate(formEndDateCalculated);
  }, [formEndDateCalculated]);

  // Limpiar overrides cuando cambia el programa (las semanas son distintas)
  useEffect(() => {
    setFormWeekDateOverrides({});
  }, [formProgramId]);

  // Opciones dinámicas de rutas y categorías a partir de las cohortes cargadas
  const programOptions = useMemo(() => {
    const map = new Map<string, string>();
    cm.filteredCohorts.forEach(c => {
      if (c.program_id) map.set(c.program_id, c.program_name || 'Sin nombre');
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [cm.filteredCohorts]);

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    cm.filteredCohorts.forEach(c => {
      if ((c as any).program_category) set.add((c as any).program_category as string);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cm.filteredCohorts]);

  const hasActiveFilters =
    !!searchText.trim() ||
    filterStatus !== 'all' ||
    filterModality !== 'cohort' ||
    filterProgram !== 'all' ||
    filterCategory !== 'all' ||
    filterVisibility !== 'all' ||
    filterEnrollment !== 'all' ||
    filterTimeframe !== 'all';

  const clearFilters = () => {
    setSearchText('');
    setFilterStatus('all');
    setFilterModality('cohort');
    setFilterProgram('all');
    setFilterCategory('all');
    setFilterVisibility('all');
    setFilterEnrollment('all');
    setFilterTimeframe('all');
  };

  // Filtered cohorts for list view (search + filtros)
  const displayCohorts = useMemo(() => {
    let list = cm.filteredCohorts;
    if (filterStatus !== 'all') list = list.filter(c => (c.status || 'draft') === filterStatus);
    if (filterModality !== 'all') list = list.filter(c => ((c as any).program_modality || 'cohort') === filterModality);
    if (filterProgram !== 'all') list = list.filter(c => c.program_id === filterProgram);
    if (filterCategory !== 'all') list = list.filter(c => ((c as any).program_category || '') === filterCategory);
    if (filterVisibility !== 'all') list = list.filter(c => (filterVisibility === 'public' ? c.is_public : !c.is_public));
    if (filterEnrollment !== 'all') list = list.filter(c => (filterEnrollment === 'open' ? !!c.enrollment_open : !c.enrollment_open));
    if (filterTimeframe !== 'all') {
      const today = new Date().toISOString().slice(0, 10);
      list = list.filter(c => {
        if ((c as any).is_perpetual) return filterTimeframe === 'ongoing';
        const start = c.start_date;
        const end = c.end_date;
        if (filterTimeframe === 'upcoming') return !!start && start > today;
        if (filterTimeframe === 'finished') return !!end && end < today;
        // ongoing
        return (!start || start <= today) && (!end || end >= today);
      });
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.program_name || '').toLowerCase().includes(q) ||
        (c.organization_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [cm.filteredCohorts, filterStatus, filterModality, filterProgram, filterCategory, filterVisibility, filterEnrollment, filterTimeframe, searchText]);

  // Local schedule state for editing
  const [localSchedule, setLocalSchedule] = useState<Partial<ScheduleItemRow>[]>([]);
  const [scheduleInitialized, setScheduleInitialized] = useState(false);

  // Conteos para la confirmación de sincronización con Google Calendar
  // Misma regla que sync-cohort-calendar: una sesión es pasada al terminar su hora de fin.
  const upcomingSessionsCount = localSchedule.filter(i => i.scheduled_date && !sessionHasEnded(i)).length;
  const pastSessionsCount = localSchedule.length - upcomingSessionsCount;

  // Confirmación explícita antes de sincronizar tras guardar cohorte/participantes
  const [pendingSync, setPendingSync] = useState<{ cohortId: string; upcoming: number; participants: number; reason: string } | null>(null);

  // Sync schedule from query
  if (scheduleQuery.data && !scheduleInitialized && editingCohortId) {
    setLocalSchedule(scheduleQuery.data);
    setScheduleInitialized(true);
  }

  // ── Edit-mode local fields ──
  const [editName, setEditName] = useState('');
  const [editProgramId, setEditProgramId] = useState('');
  const [editStartDate, setEditStartDate] = useState<Date | undefined>(undefined);
  const [editEndDate, setEditEndDate] = useState<Date | undefined>(undefined);
  const [editStatus, setEditStatus] = useState('draft');
  const [editDemoDayDate, setEditDemoDayDate] = useState<Date | undefined>(undefined);
  const [editDemoDayStartHour, setEditDemoDayStartHour] = useState(9);
  const [editDemoDayEndHour, setEditDemoDayEndHour] = useState(13);
  const [editDemoDayLink, setEditDemoDayLink] = useState('');
  // Meet auto-generado por Google al sincronizar (solo lectura) — distinto del
  // link manual de arriba. Antes no se mostraba en ningún lado de la plataforma.
  const [editDemoDayMeetLink, setEditDemoDayMeetLink] = useState('');
  const [editDemoDayRecordingLink, setEditDemoDayRecordingLink] = useState('');
  const [editDemoDayTranscriptUrl, setEditDemoDayTranscriptUrl] = useState('');
  const [editDemoDayTranscriptName, setEditDemoDayTranscriptName] = useState('');
  const [uploadingTranscript, setUploadingTranscript] = useState(false);
  const transcriptInputRef = useRef<HTMLInputElement>(null);
  const [editPrice, setEditPrice] = useState<string>('');
  const [editCurrency, setEditCurrency] = useState('USD');
  const [editEnrollmentOpen, setEditEnrollmentOpen] = useState(false);
  const [editDemoDayEnabled, setEditDemoDayEnabled] = useState(true);
  const [editProjectDeadline, setEditProjectDeadline] = useState<Date | undefined>(undefined);
  const [editProjectDeadlineWeek, setEditProjectDeadlineWeek] = useState<string>('');
  const [editChallengeLabDeadline, setEditChallengeLabDeadline] = useState<Date | undefined>(undefined);
  const [editChallengeLabDeadlineWeek, setEditChallengeLabDeadlineWeek] = useState<string>('');
  const [editContentAccessDeadline, setEditContentAccessDeadline] = useState<Date | undefined>(undefined);
  const [editOrgId, setEditOrgId] = useState<string>('');
  const [editIsPublic, setEditIsPublic] = useState<boolean>(false);
  // Periodicidad usada al recalcular fechas vacías de sesiones desde Edit > General.
  const [editRecalcPeriodicity, setEditRecalcPeriodicity] = useState<number>(7);

  // ── Open Edit ──
  const openEditView = (cohort: CohortRow) => {
    setEditingCohortId(cohort.id);
    setEditName(cohort.name);
    setEditProgramId(cohort.program_id);
    setEditStartDate(parseLocalDate(cohort.start_date));
    setEditEndDate(parseLocalDate(cohort.end_date));
    setEditStatus(cohort.status || 'draft');
    setEditDemoDayDate(parseLocalDate(cohort.demo_day_date));
    setEditDemoDayStartHour(cohort.demo_day_start_hour ?? 9);
    setEditDemoDayEndHour(cohort.demo_day_end_hour ?? 13);
    setEditDemoDayLink(cohort.demo_day_link || '');
    setEditDemoDayRecordingLink((cohort as any).demo_day_recording_link || '');
    setEditDemoDayTranscriptUrl((cohort as any).demo_day_transcript_url || '');
    setEditDemoDayTranscriptName((cohort as any).demo_day_transcript_name || '');
    setEditPrice(cohort.price != null ? cohort.price.toString() : '');
    setEditCurrency(cohort.currency || 'USD');
    setEditEnrollmentOpen(cohort.enrollment_open ?? false);
    setEditDemoDayEnabled(cohort.demo_day_enabled ?? true);
    setEditProjectDeadline(parseLocalDate(cohort.project_deadline));
    setEditProjectDeadlineWeek(cohort.project_deadline_week != null ? String(cohort.project_deadline_week) : '');
    setEditChallengeLabDeadline(parseLocalDate(cohort.challenge_lab_deadline));
    setEditChallengeLabDeadlineWeek(cohort.challenge_lab_deadline_week != null ? String(cohort.challenge_lab_deadline_week) : '');
    setEditContentAccessDeadline(parseLocalDate(cohort.content_access_deadline));
    setEditDemoDayMeetLink((cohort as any).demo_day_meet_link || '');
    setEditOrgId((cohort as any).organization_id || '');
    setEditIsPublic(!!(cohort as any).is_public);
    setEditTab('general');
    setScheduleInitialized(false);
    setLocalSchedule([]);
  };

  const closeEdit = () => {
    setEditingCohortId(null);
    setScheduleInitialized(false);
    setLocalSchedule([]);
  };

  // ── Create ──
  const openCreateDialog = () => {
    setFormName('');
    setFormProgramId('');
    setFormOrgId(cm.isSuperAdmin ? '' : (cm.userOrgId || ''));
    setFormStartDate(undefined);
    setFormEndDate(undefined);
    setFormDemoDayDate(undefined);
    setFormDemoDayStartHour(9);
    setFormDemoDayEndHour(13);
    setFormIsPublic(false);
    setFormSessionPeriodicity(7);
    setFormWeekDateOverrides({});
    setIsCreateDialogOpen(true);
  };

  const handleCreateCohort = async () => {
    if (!isSuperAdmin) {
      toast({ title: 'Permiso denegado', description: 'Solo Super Admin puede crear cohortes.', variant: 'destructive' });
      return;
    }
    const endDate = formEndDate ?? formEndDateCalculated;
    if (!formName || !formProgramId) {
      toast({ title: "Error", description: "Completa nombre y ruta.", variant: "destructive" });
      return;
    }
    // Profesional Líder IA: fechas opcionales. El admin las asigna luego desde edición.
    if (!isLeaderProfessionalProgram && !formStartDate) {
      toast({ title: "Error", description: "Selecciona la fecha de inicio.", variant: "destructive" });
      return;
    }
    if (!isLeaderProfessionalProgram && !endDate) {
      toast({ title: "Error", description: "Selecciona una fecha de fin, o elige una ruta con semanas numeradas para calcularla automáticamente.", variant: "destructive" });
      return;
    }
    try {
      await cm.createCohort.mutateAsync({
        name: formName,
        program_id: formProgramId,
        organization_id: formIsPublic ? null : (formOrgId || null),
        start_date: formStartDate ? format(formStartDate, 'yyyy-MM-dd') : null,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
        demo_day_date: formDemoDayDate ? format(formDemoDayDate, 'yyyy-MM-dd') : undefined,
        demo_day_start_hour: formDemoDayStartHour,
        demo_day_end_hour: formDemoDayEndHour,
        is_public: formIsPublic,
        status: 'draft',
        session_periodicity_days: formSessionPeriodicity,
        week_date_overrides: Object.keys(formWeekDateOverrides).length > 0
          ? Object.fromEntries(
              Object.entries(formWeekDateOverrides).map(([k, v]) => [k, v ? format(v, 'yyyy-MM-dd') : null])
            )
          : undefined,
      });
      setIsCreateDialogOpen(false);
      const successDesc = formStartDate
        ? `"${formName}" creada con calendario auto-generado.`
        : `"${formName}" creada. Asígnale fecha de inicio desde edición para generar el calendario.`;
      toast({ title: "Cohorte creada", description: successDesc });
    } catch (err: any) {
      const msg: string = err?.message || '';
      let description = msg;
      if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('policy'))
        description = 'No tienes permisos para crear cohortes en esta organización.';
      else if (msg.includes('duplicate key') || msg.includes('already exists'))
        description = 'Ya existe una cohorte con ese nombre. Elige otro nombre.';
      else if (msg.includes('foreign key') || msg.includes('violates foreign'))
        description = 'La ruta o la organización seleccionada ya no existe.';
      else if (msg.includes('JWT') || msg.includes('token is expired') || msg.includes('session_not_found'))
        description = 'Tu sesión expiró. Recarga la página e inicia sesión de nuevo.';
      else if (msg.includes('Failed to fetch') || msg.includes('FetchError') || msg.includes('fetch failed'))
        description = 'Error de conexión. Verifica tu internet e inténtalo de nuevo.';
      else if (msg.includes('Invalid date') || msg.includes('RangeError'))
        description = 'Una de las fechas no es válida. Revisa las fechas seleccionadas.';
      else if (!description)
        description = 'Ocurrió un error inesperado. Inténtalo de nuevo.';
      toast({ title: 'Error al crear la cohorte', description, variant: 'destructive' });
    }
  };

  // ── Save Edit ──
  // Persiste cohorte + cronograma sin pedir confirmación de sync ni mostrar su
  // propio toast — lo reusan tanto "Guardar" como los botones de sincronizar,
  // para que sincronizar ya no requiera guardar por separado primero.
  const persistCohortAndSchedule = async (): Promise<Partial<ScheduleItemRow>[]> => {
    if (!editingCohortId) return localSchedule;
    await cm.updateCohort.mutateAsync({
      id: editingCohortId,
      updates: {
        name: editName,
        program_id: editProgramId,
        start_date: editStartDate ? format(editStartDate, 'yyyy-MM-dd') : null,
        end_date: editEndDate ? format(editEndDate, 'yyyy-MM-dd') : null,
        status: editStatus,
        demo_day_date: editDemoDayDate ? format(editDemoDayDate, 'yyyy-MM-dd') : null,
        demo_day_start_hour: editDemoDayStartHour,
        demo_day_end_hour: editDemoDayEndHour,
        demo_day_link: editDemoDayLink || null,
        demo_day_enabled: editDemoDayEnabled,
        demo_day_recording_link: editDemoDayRecordingLink || null,
        demo_day_transcript_url: editDemoDayTranscriptUrl || null,
        demo_day_transcript_name: editDemoDayTranscriptName || null,
        project_deadline: editProjectDeadline ? format(editProjectDeadline, 'yyyy-MM-dd') : null,
        project_deadline_week: editProjectDeadlineWeek ? parseInt(editProjectDeadlineWeek) : null,
        challenge_lab_deadline: editChallengeLabDeadline ? format(editChallengeLabDeadline, 'yyyy-MM-dd') : null,
        challenge_lab_deadline_week: editChallengeLabDeadlineWeek ? parseInt(editChallengeLabDeadlineWeek) : null,
        content_access_deadline: editContentAccessDeadline ? format(editContentAccessDeadline, 'yyyy-MM-dd') : null,
        price: editPrice ? parseFloat(editPrice) : null,
        currency: editCurrency,
        enrollment_open: editEnrollmentOpen,
        organization_id: editIsPublic ? null : (editOrgId || null),
        is_public: editIsPublic,
      } as any,
    });
    await cm.upsertScheduleItems.mutateAsync({
      cohort_id: editingCohortId,
      items: localSchedule,
    });
    // Refetch schedule from DB and reload local state so newly-inserted
    // rows get their id AND user edits don't get clobbered by stale cache.
    const { data: fresh } = await scheduleQuery.refetch();
    if (fresh) {
      setLocalSchedule(fresh);
      setScheduleInitialized(true);
    }
    return fresh || localSchedule;
  };

  const handleSaveEdit = async () => {
    if (!editingCohortId) return;
    if (!isSuperAdmin) {
      toast({ title: 'Permiso denegado', description: 'Solo Super Admin puede editar cohortes.', variant: 'destructive' });
      return;
    }
    try {
      // Solo se propone sincronizar si el guardado cambia algo que llega a Google
      // Calendar (sesiones, Demo Day o el nombre, que va en el título del evento).
      // Cambiar la fecha de inicio, plazos u otros datos de la cohorte no lo afecta.
      const c = editingCohort as any;
      const calendarSignature = (items: Partial<ScheduleItemRow>[], cohortFields: unknown[]) =>
        JSON.stringify({
          items: items
            .map((i: any) => [i.id ?? '', i.title ?? '', i.scheduled_date ?? '', i.start_hour ?? '', i.start_minute ?? '',
              i.end_hour ?? '', i.end_minute ?? '', (i.connection_link ?? '').trim(), i.expert_id ?? ''].join('|'))
            .sort(),
          cohortFields,
        });
      const before = calendarSignature(scheduleQuery.data || [], [
        // Mismos defaults que openEditView (9 / 13) para no detectar cambios falsos.
        c?.name ?? '', c?.demo_day_enabled ?? true, c?.demo_day_date ?? '', c?.demo_day_start_hour ?? 9,
        c?.demo_day_end_hour ?? 13, c?.demo_day_link || '',
      ]);
      const after = calendarSignature(localSchedule, [
        editName, editDemoDayEnabled, editDemoDayDate ? format(editDemoDayDate, 'yyyy-MM-dd') : '',
        editDemoDayStartHour ?? '', editDemoDayEndHour ?? '', editDemoDayLink || '',
      ]);
      const calendarChanged = before !== after;

      const fresh = await persistCohortAndSchedule();
      toast({ title: "Cohorte actualizada", description: "Cambios guardados correctamente." });
      // Sincronización con Google Calendar: ya no se lanza en silencio. Se pide
      // confirmación indicando cuántas sesiones y participantes se verán afectados.
      const futureItems = fresh.filter(
        (i: any) => i.scheduled_date && !sessionHasEnded(i)
      );
      if (calendarChanged && futureItems.length > 0 && !cohortFinishedForSync) {
        setPendingSync({
          cohortId: editingCohortId,
          upcoming: futureItems.length,
          participants: (editingCohort as any)?.participants_count ?? 0,
          reason: 'Guardaste cambios en la cohorte.',
        });
      }
    } catch (err: any) {
      toast({ title: "Error al guardar", description: err.message, variant: "destructive" });
    }
  };

  // Guarda silenciosamente los cambios pendientes (cronograma + cohorte) justo
  // antes de sincronizar — así "Sincronizar" ya no exige un "Guardar" previo.
  const saveBeforeSync = async (): Promise<boolean> => {
    if (!editingCohortId) return false;
    try {
      await persistCohortAndSchedule();
      return true;
    } catch (err: any) {
      toast({ title: 'Error al guardar antes de sincronizar', description: err.message, variant: 'destructive' });
      return false;
    }
  };

  // ── Delete ──
  const handleDeleteCohort = async (id: string) => {
    if (!isSuperAdmin) {
      toast({ title: 'Permiso denegado', description: 'Solo Super Admin puede eliminar cohortes.', variant: 'destructive' });
      return;
    }
    try {
      await cm.deleteCohort.mutateAsync(id);
      toast({ title: "Cohorte eliminada" });
    } catch (err: any) {
      toast({ title: "Error al eliminar", description: err.message, variant: "destructive" });
    }
  };

  // ── Schedule item update / add / remove ──
  const updateScheduleItem = (index: number, updates: Partial<ScheduleItemRow>) => {
    setLocalSchedule(prev => prev.map((item, i) => i === index ? { ...item, ...updates } : item));
  };

  // Tras sincronizar con Calendar/Fireflies, la vista sigue mostrando el borrador
  // local (localSchedule solo se carga una vez por scheduleInitialized, para no
  // pisar ediciones sin guardar) — sin esto, el link de Meet recién generado no
  // aparecía hasta recargar la página. Solo se traen y mergean los campos que el
  // sync realmente escribe; el resto del borrador local (título, fecha, experto…)
  // queda intacto.
  const refreshCalendarFields = async () => {
    if (!editingCohortId) return;
    const { data: fresh } = await supabase
      .from('cohort_schedule_items')
      .select('id, google_event_id, meet_link, connection_link, recording_link, recording_status, last_recording_check, meet_recording_link')
      .eq('cohort_id', editingCohortId);
    if (!fresh) return;
    const byId = new Map(fresh.map((r: any) => [r.id, r]));
    setLocalSchedule(prev => prev.map((item: any) => {
      const f = item.id ? byId.get(item.id) : null;
      return f ? { ...item, ...f } : item;
    }));
  };

  // Mismo problema que refreshCalendarFields pero para el Demo Day: el Meet
  // auto-generado se guarda en cohorts.demo_day_meet_link y el borrador local
  // (editDemoDay*) solo se carga una vez al abrir la cohorte.
  const refreshDemoDayMeetLink = async () => {
    if (!editingCohortId) return;
    const { data } = await supabase
      .from('cohorts')
      .select('demo_day_meet_link')
      .eq('id', editingCohortId)
      .maybeSingle();
    setEditDemoDayMeetLink((data as any)?.demo_day_meet_link || '');
  };

  const removeScheduleItem = (index: number) => {
    setLocalSchedule(prev => prev.filter((_, i) => i !== index));
  };

  const addCohortMentorship = () => {
    if (!editingCohortId) return;
    const nextWeek = Math.max(0, ...localSchedule.map(i => i.week_number ?? 0)) + 1;
    setLocalSchedule(prev => [
      ...prev,
      {
        cohort_id: editingCohortId,
        type: 'mentorship',
        ref_id: null,
        title: 'Nueva mentoría',
        description: '',
        week_number: nextWeek,
        scheduled_date: null,
        start_hour: null,
        start_minute: null,
        end_hour: null,
        end_minute: null,
        connection_link: null,
        expert_id: null,
        recording_link: null,
      } as Partial<ScheduleItemRow>,
    ]);
    toast({
      title: 'Sesión de mentoría agregada',
      description: 'Completa los datos y guarda los cambios para confirmar.',
    });
  };



  // ── Participants ──
  // Top-level queries for assign dialog
  const assignParticipantsQuery = cm.useCohortParticipants(assigningCohortId);
  const assignOrgId = assigningCohort?.organization_id || (cm.isSuperAdmin ? null : cm.userOrgId);
  const assignProfilesQuery = cm.useAvailableProfiles(assignOrgId);

  // Inicializa selectedParticipantIds una sola vez por apertura del diálogo,
  // y solo cuando ya hay data. Usa un ref para: (1) repoblar los ya asignados
  // aunque la data venga de caché (misma referencia: el effect igual se evalúa
  // al abrir vía isAssignDialogOpen), y (2) no pisar la selección manual del
  // usuario si la query se revalida con el diálogo abierto.
  const participantsInitializedFor = useRef<string | null>(null);
  useEffect(() => {
    if (
      isAssignDialogOpen &&
      assigningCohortId &&
      assignParticipantsQuery.data &&
      participantsInitializedFor.current !== assigningCohortId
    ) {
      setSelectedParticipantIds(assignParticipantsQuery.data.map(p => p.user_id));
      participantsInitializedFor.current = assigningCohortId;
    }
  }, [isAssignDialogOpen, assigningCohortId, assignParticipantsQuery.data]);

  const handleOpenAssignDialog = (cohort: CohortRow) => {
    participantsInitializedFor.current = null; // permite reinicializar al reabrir
    setAssigningCohortId(cohort.id);
    setParticipantSearch('');
    setSelectedParticipantIds([]); // se repuebla por el effect en cuanto haya data (fresca o de caché)
    setIsAssignDialogOpen(true);
  };

  const handleImportFile = async (file: File) => {
    if (!editingCohortId) return;
    setImportLoading(true);
    try {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (rows.length === 0) throw new Error('Archivo vacío');

      const header = (rows[0] as string[]).map(h => String(h || '').toLowerCase());
      let emailCol = header.findIndex(h => h.includes('email') || h.includes('correo') || h.includes('mail'));
      if (emailCol < 0) emailCol = 0;

      const emails = rows.slice(1)
        .map(r => String((r as any[])[emailCol] || '').trim())
        .filter(e => e.includes('@'));

      if (emails.length === 0) throw new Error('No se encontraron correos válidos en el archivo');

      const result = await cm.importParticipantsByEmail.mutateAsync({
        cohort_id: editingCohortId,
        emails,
      });

      let desc = `Se agregaron ${result.added} participante(s).`;
      if (result.notFound.length > 0) {
        desc += ` ${result.notFound.length} correo(s) no encontrados: ${result.notFound.join(', ')}`;
      }
      toast({ title: 'Importación completada', description: desc });
      setIsImportDialogOpen(false);
    } catch (err: any) {
      toast({ title: 'Error al importar', description: err.message, variant: 'destructive' });
    } finally {
      setImportLoading(false);
    }
  };

  const toggleParticipant = (uid: string) => {
    setSelectedParticipantIds(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleSaveParticipants = async () => {
    if (!assigningCohortId) return;
    const cohortId = assigningCohortId;
    try {
      const result = await cm.syncParticipants.mutateAsync({
        cohort_id: cohortId,
        user_ids: selectedParticipantIds,
      });
      setIsAssignDialogOpen(false);
      setAssigningCohortId(null);
      toast({ title: "Participantes actualizados", description: `${selectedParticipantIds.length} participantes asignados.` });

      // Si hubo altas o bajas, se ofrece re-sincronizar Google Calendar con
      // confirmación explícita (antes se hacía en silencio y generaba correos masivos).
      if (result?.changed) {
        const { data: upcomingRows } = await supabase
          .from('cohort_schedule_items')
          .select('scheduled_date, end_hour, end_minute')
          .eq('cohort_id', cohortId)
          .not('scheduled_date', 'is', null);
        const count = (upcomingRows || []).filter(i => !sessionHasEnded(i)).length;
        if (count > 0) {
          setPendingSync({
            cohortId,
            upcoming: count || 0,
            participants: selectedParticipantIds.length,
            reason: 'Cambiaste la lista de participantes.',
          });
        }
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const assignFilteredProfiles = (assignProfilesQuery.data || []).filter(p =>
    p.full_name.toLowerCase().includes(participantSearch.toLowerCase()) ||
    p.email.toLowerCase().includes(participantSearch.toLowerCase()) ||
    (p.area || '').toLowerCase().includes(participantSearch.toLowerCase())
  );

  // ── Mentors ──
  const assignMentorsQuery = cm.useCohortMentors(assigningMentorCohortId);
  const availableMentorsQuery = cm.useAvailableMentors();

  useEffect(() => {
    if (assigningMentorCohortId && assignMentorsQuery.data) {
      setSelectedMentorIds(assignMentorsQuery.data.map(m => m.user_id));
    }
  }, [assigningMentorCohortId, assignMentorsQuery.data]);

  const handleOpenMentorDialog = (cohort: CohortRow) => {
    setAssigningMentorCohortId(cohort.id);
    setMentorSearch('');
    setSelectedMentorIds([]);
    setIsMentorDialogOpen(true);
  };

  const toggleMentor = (uid: string) => {
    setSelectedMentorIds(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleSaveMentors = async () => {
    if (!assigningMentorCohortId) return;
    try {
      await cm.syncMentors.mutateAsync({
        cohort_id: assigningMentorCohortId,
        user_ids: selectedMentorIds,
      });
      setIsMentorDialogOpen(false);
      setAssigningMentorCohortId(null);
      toast({ title: "Mentores actualizados", description: `${selectedMentorIds.length} mentores asignados.` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const filteredMentors = (availableMentorsQuery.data || []).filter(m =>
    m.full_name.toLowerCase().includes(mentorSearch.toLowerCase()) ||
    m.email.toLowerCase().includes(mentorSearch.toLowerCase())
  );

  // ── Auditores (clon del patrón de mentores) ──
  const assignAuditorsQuery = cm.useCohortAuditors(assigningAuditorCohortId);
  const availableAuditorsQuery = cm.useAvailableAuditors();

  useEffect(() => {
    if (assigningAuditorCohortId && assignAuditorsQuery.data) {
      setSelectedAuditorIds(assignAuditorsQuery.data.map(a => a.user_id));
    }
  }, [assigningAuditorCohortId, assignAuditorsQuery.data]);

  const handleOpenAuditorDialog = (cohort: CohortRow) => {
    setAssigningAuditorCohortId(cohort.id);
    setAuditorSearch('');
    setSelectedAuditorIds([]);
    setIsAuditorDialogOpen(true);
  };

  const toggleAuditor = (uid: string) => {
    setSelectedAuditorIds(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleSaveAuditors = async () => {
    if (!assigningAuditorCohortId) return;
    try {
      await cm.syncAuditors.mutateAsync({
        cohort_id: assigningAuditorCohortId,
        user_ids: selectedAuditorIds,
      });
      setIsAuditorDialogOpen(false);
      setAssigningAuditorCohortId(null);
      toast({ title: "Auditores actualizados", description: `${selectedAuditorIds.length} auditores asignados.` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const filteredAuditors = (availableAuditorsQuery.data || []).filter(a =>
    a.full_name.toLowerCase().includes(auditorSearch.toLowerCase()) ||
    a.email.toLowerCase().includes(auditorSearch.toLowerCase())
  );

  // Descarga CSV de asistencia (Meet o Fireflies según lo que tenga la sesión).
  const downloadAttendanceCSV = (item: any) => {
    const attendees: any[] = Array.isArray(item?.attendees) ? item.attendees : [];
    if (!attendees.length) return;
    const escape = (v: any) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const fmtTime = (iso: string | null | undefined) => {
      if (!iso) return '';
      const d = new Date(iso);
      return isNaN(d.getTime()) ? '' : d.toLocaleString('es-CO', { timeZone: 'America/Bogota' });
    };
    const header = ['Nombre', 'Email', 'Ubicación', 'Entró', 'Salió', 'Sesión', 'Fecha', 'Duración (min)'];
    const rows = attendees.map(a => [
      a.name || a.displayName || '',
      a.email || '',
      a.location || '',
      fmtTime(a.joined_at),
      fmtTime(a.left_at),
      item.title || '',
      item.scheduled_date || '',
      item.duration_minutes ?? '',
    ]);
    const csv = [header, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asistencia_${(item.title || 'sesion').replace(/[^a-z0-9]+/gi, '_')}_${item.scheduled_date || ''}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Render schedule card helper ──

  const renderScheduleCard = (item: Partial<ScheduleItemRow>, globalIdx: number, type: 'workshop' | 'mentorship') => {
    const isWorkshop = type === 'workshop';
    // Las mentorías son de la cohorte (no heredadas de la ruta): todas editables.
    const canEditMentorship = !isWorkshop && isSuperAdmin;
    return (
      <Card key={globalIdx} className="border-border/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={cn("flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm shrink-0", isWorkshop ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary")}>
              S{item.week_number || '?'}
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <div className="space-y-1 w-16">
                    <Label className="text-[10px] text-muted-foreground">Semana</Label>
                    {canEditMentorship ? (
                      <Input
                        type="number"
                        min={1}
                        className="h-8 text-sm text-center px-1"
                        value={item.week_number ?? ''}
                        onChange={e => updateScheduleItem(globalIdx, { week_number: e.target.value === '' ? null : parseInt(e.target.value) })}
                      />
                    ) : (
                      <div className="h-8 flex items-center justify-center text-sm font-medium">{item.week_number || '?'}</div>
                    )}
                  </div>
                  {canEditMentorship ? (
                    <Input
                      className="h-8 text-sm flex-1"
                      value={item.title || ''}
                      onChange={e => updateScheduleItem(globalIdx, { title: e.target.value })}
                      placeholder="Título de la mentoría"
                    />
                  ) : (
                    <span className="text-sm font-medium max-w-xs truncate">{item.title || 'Sin título'}</span>
                  )}
                </div>
                {canEditMentorship && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      removeScheduleItem(globalIdx);
                      toast({ title: 'Sesión eliminada', description: 'Guarda los cambios para confirmar.' });
                    }}
                    title="Eliminar mentoría"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {canEditMentorship && (
                <div className="space-y-1">
                  <Label className="text-xs">📝 Descripción</Label>
                  <Textarea
                    className="text-sm min-h-[60px]"
                    value={item.description || ''}
                    onChange={e => updateScheduleItem(globalIdx, { description: e.target.value })}
                    placeholder="Describe el objetivo y contenido de esta mentoría"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">📅 Fecha</Label>
                  <div className="flex items-center gap-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="flex-1 justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-3 w-3" />
                          {item.scheduled_date ? format(new Date(item.scheduled_date + 'T00:00:00'), 'dd MMM yyyy', { locale: es }) : 'Sin fecha'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={item.scheduled_date ? new Date(item.scheduled_date + 'T00:00:00') : undefined} onSelect={d => d && updateScheduleItem(globalIdx, { scheduled_date: format(d, 'yyyy-MM-dd') })} className="p-3 pointer-events-auto" />
                      </PopoverContent>
                    </Popover>
                    {item.scheduled_date && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title="Quitar fecha"
                        onClick={() => updateScheduleItem(globalIdx, { scheduled_date: null })}
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">⏰ Hora Inicio</Label>
                  <div className="flex gap-1">
                    <Select value={(item.start_hour ?? '').toString()} onValueChange={v => updateScheduleItem(globalIdx, { start_hour: parseInt(v) })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{hours.map(h => <SelectItem key={h.value} value={h.value.toString()}>{h.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select
                      value={((item.start_minute ?? 0)).toString().padStart(2, '0')}
                      onValueChange={v => updateScheduleItem(globalIdx, { start_minute: parseInt(v) })}
                    >
                      <SelectTrigger className="h-8 text-xs w-16"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[0,5,10,15,20,25,30,35,40,45,50,55].map(m => (
                          <SelectItem key={m} value={m.toString().padStart(2,'0')}>{m.toString().padStart(2,'0')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">⏰ Hora Fin</Label>
                  <div className="flex gap-1">
                    <Select value={(item.end_hour ?? '').toString()} onValueChange={v => updateScheduleItem(globalIdx, { end_hour: parseInt(v) })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{hours.map(h => <SelectItem key={h.value} value={h.value.toString()}>{h.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select
                      value={((item.end_minute ?? 0)).toString().padStart(2, '0')}
                      onValueChange={v => updateScheduleItem(globalIdx, { end_minute: parseInt(v) })}
                    >
                      <SelectTrigger className="h-8 text-xs w-16"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[0,5,10,15,20,25,30,35,40,45,50,55].map(m => (
                          <SelectItem key={m} value={m.toString().padStart(2,'0')}>{m.toString().padStart(2,'0')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">🔗 Link de conexión (Meet)</Label>
                  <Input className="h-8 text-sm" value={item.connection_link || ''} onChange={e => updateScheduleItem(globalIdx, { connection_link: e.target.value })} placeholder="Se genera al sincronizar con Calendar" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{isWorkshop ? '👨‍🏫 Experto' : '👨‍🏫 Mentor'}</Label>
                  <Select value={item.expert_id || ''} onValueChange={v => updateScheduleItem(globalIdx, { expert_id: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={isWorkshop ? 'Seleccionar experto' : 'Seleccionar mentor'} /></SelectTrigger>
                    <SelectContent>{cm.experts.map(exp => <SelectItem key={exp.id} value={exp.id}>{exp.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {/* Input Grabación Fireflies oculto — Meet es la fuente visible.
                    El campo recording_link se sigue actualizando automáticamente
                    desde sync-fireflies-recordings para enriquecer attendees. */}
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">🎥 Grabación Meet (Drive)</Label>
                  <Input className="h-8 text-sm" value={(item as ScheduleItemRow).meet_recording_link || ''} onChange={e => updateScheduleItem(globalIdx, { meet_recording_link: e.target.value })} placeholder="Auto desde Google Calendar · editable" />
                </div>
              </div>
              {/* Sync status badges */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {(item as any).google_event_id ? (
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 gap-1">
                    <CalendarLucide className="w-3 h-3" /> Sincronizado en Calendar
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground gap-1">Sin sincronizar</Badge>
                )}
                {(item as any).id && sessionHasEnded(item) && (
                  <Badge variant="outline" className="text-muted-foreground gap-1">Sesión finalizada · no se sincroniza</Badge>
                )}
                {(item as any).id && !sessionHasEnded(item) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs gap-1"
                    disabled={!editingCohortId || calSync.syncingItemId === (item as any).id}
                    onClick={async () => {
                      if (!editingCohortId) return;
                      if (!(await saveBeforeSync())) return;
                      calSync.syncItem(editingCohortId, (item as any).id).then(refreshCalendarFields).catch(() => {});
                    }}
                  >
                    {calSync.syncingItemId === (item as any).id
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <RefreshCw className="w-3 h-3" />}
                    Sincronizar esta sesión
                  </Button>
                )}
                {(() => {
                  if (!(item as any).meet_link) return null;
                  // Ocultar Abrir Meet y Enviar Fireflies si la sesion ya termino
                  const sd = (item as any).scheduled_date;
                  if (sd) {
                    const now = new Date();
                    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    if (sd < today) return null;
                    if (sd === today) {
                      const currentMinutes = now.getHours() * 60 + now.getMinutes();
                      const endMinutes = ((item as any).end_hour ?? 23) * 60 + ((item as any).end_minute ?? 59);
                      if (currentMinutes >= endMinutes) return null;
                    }
                  }
                  return (
                    <>
                      <a href={(item as any).meet_link} target="_blank" rel="noreferrer">
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 gap-1 cursor-pointer">
                          <Video className="w-3 h-3" /> Abrir Meet <ExternalLink className="w-3 h-3" />
                        </Badge>
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const { data, error } = await supabase.functions.invoke('fireflies-join-meeting', {
                              body: { schedule_item_id: (item as any).id },
                            });
                            if (error) throw error;
                            if (data?.error) throw new Error(data.error);
                            toast({ title: 'Fireflies enviado', description: 'El notetaker se unirá al Meet en unos segundos.' });
                          } catch (e: any) {
                            toast({ title: 'Error', description: e.message, variant: 'destructive' });
                          }
                        }}
                      >
                        <Badge variant="outline" className="bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30 gap-1 cursor-pointer">
                          🤖 {(item as any).fireflies_bot_sent_at ? 'Reenviar Fireflies' : 'Enviar Fireflies ahora'}
                        </Badge>
                      </button>
                    </>
                  );
                })()}
                {/* ── Grabación Meet (Google Drive) — fuente principal ── */}
                {item.meet_recording_link ? (
                  <a href={item.meet_recording_link} target="_blank" rel="noreferrer">
                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 gap-1 cursor-pointer">
                      <Video className="w-3 h-3" /> Grabación Meet
                    </Badge>
                  </a>
                ) : (item as any).recording_status === 'not_found' ? (
                  <Badge variant="outline" className="text-muted-foreground">Sin grabación Meet</Badge>
                ) : (item as any).google_event_id && (
                  <Badge variant="outline" className="text-muted-foreground text-[10px]">Meet: esperando…</Badge>
                )}

                {/* Grabación Fireflies y transcripción ocultas — Meet es la fuente visible.
                    Backend sigue guardando recording_link/transcript_url para enriquecer attendees. */}

                {/* ── Asistencia: reporte Drive (Meet) ── */}
                {(item as any).attendance_report_link && (
                  <a href={(item as any).attendance_report_link} target="_blank" rel="noreferrer">
                    <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 gap-1 cursor-pointer">
                      📊 Reporte asistencia (Meet) <ExternalLink className="w-3 h-3" />
                    </Badge>
                  </a>
                )}

                {/* ── Asistencia: solo se muestra cuando viene de Meet (tiene joined_at) ── */}
                {Array.isArray((item as any).attendees)
                  && (item as any).attendees.length > 0
                  && (item as any).attendees.some((a: any) => a?.joined_at) && (
                  <>
                    <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/30 gap-1">
                      👥 {(item as any).attendees_count || (item as any).attendees.length} asistentes (Meet)
                      {(item as any).duration_minutes ? ` · ${(item as any).duration_minutes} min` : ''}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => downloadAttendanceCSV(item)}
                      className="text-xs text-primary hover:underline"
                    >
                      Descargar asistencia (CSV)
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ── SHARED DIALOGS (participants & mentors) ──
  const sharedDialogs = (
    <>
      {/* Assign Participants Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Asignar Participantes
            </DialogTitle>
            <DialogDescription>
              {assigningCohort && <>Selecciona participantes para <span className="font-semibold">{assigningCohort.name}</span></>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre, email o área..." value={participantSearch} onChange={e => setParticipantSearch(e.target.value)} className="pl-10" />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{selectedParticipantIds.length} seleccionados</span>
            </div>
            <ScrollArea className="h-[400px] border rounded-lg">
              <div className="p-2 space-y-1">
                {assignProfilesQuery.isLoading ? (
                  <div className="space-y-2 p-4">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : assignFilteredProfiles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No se encontraron participantes</div>
                ) : assignFilteredProfiles.map(p => {
                  const isSelected = selectedParticipantIds.includes(p.id);
                  return (
                    <div key={p.id} className={cn("flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors", isSelected ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/50")} onClick={() => toggleParticipant(p.id)}>
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleParticipant(p.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.full_name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{p.email}</span>
                        </div>
                      </div>
                      {p.area && <Badge variant="outline" className="shrink-0">{p.area}</Badge>}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveParticipants} disabled={cm.syncParticipants.isPending} className="gap-2">
              <CheckCircle2 className="w-4 h-4" />Guardar ({selectedParticipantIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Assign Mentors Dialog */}
      <Dialog open={isMentorDialogOpen} onOpenChange={setIsMentorDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-secondary" />
              Asignar Mentores
            </DialogTitle>
            <DialogDescription>
              {assigningMentorCohort && <>Selecciona mentores para <span className="font-semibold">{assigningMentorCohort.name}</span></>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre o email..." value={mentorSearch} onChange={e => setMentorSearch(e.target.value)} className="pl-10" />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{selectedMentorIds.length} seleccionados</span>
            </div>
            <ScrollArea className="h-[400px] border rounded-lg">
              <div className="p-2 space-y-1">
                {availableMentorsQuery.isLoading ? (
                  <div className="space-y-2 p-4">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : filteredMentors.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No se encontraron mentores. Asegúrate de que existan usuarios con rol "mentor".</div>
                ) : filteredMentors.map(m => {
                  const isSelected = selectedMentorIds.includes(m.id);
                  return (
                    <div key={m.id} className={cn("flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors", isSelected ? "bg-secondary/10 border border-secondary/30" : "hover:bg-muted/50")} onClick={() => toggleMentor(m.id)}>
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleMentor(m.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{m.full_name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{m.email}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMentorDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveMentors} disabled={cm.syncMentors.isPending} className="gap-2">
              <CheckCircle2 className="w-4 h-4" />Guardar ({selectedMentorIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Assign Auditors Dialog (clon de Mentores) */}
      <Dialog open={isAuditorDialogOpen} onOpenChange={setIsAuditorDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Asignar Auditores
            </DialogTitle>
            <DialogDescription>
              {assigningAuditorCohort && <>Selecciona auditores (solo lectura) para <span className="font-semibold">{assigningAuditorCohort.name}</span></>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre o email..." value={auditorSearch} onChange={e => setAuditorSearch(e.target.value)} className="pl-10" />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{selectedAuditorIds.length} seleccionados</span>
            </div>
            <ScrollArea className="h-[400px] border rounded-lg">
              <div className="p-2 space-y-1">
                {availableAuditorsQuery.isLoading ? (
                  <div className="space-y-2 p-4">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : filteredAuditors.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No se encontraron auditores. Asegúrate de que existan usuarios con rol "auditor".</div>
                ) : filteredAuditors.map(a => {
                  const isSelected = selectedAuditorIds.includes(a.id);
                  return (
                    <div key={a.id} className={cn("flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors", isSelected ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/50")} onClick={() => toggleAuditor(a.id)}>
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleAuditor(a.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{a.full_name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{a.email}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAuditorDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveAuditors} disabled={cm.syncAuditors.isPending} className="gap-2">
              <CheckCircle2 className="w-4 h-4" />Guardar ({selectedAuditorIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // ── EDIT VIEW ──
  if (showCertificates) {
    return (
      <GeneratedCertificatesView
        selectedOrg={selectedOrg}
        onOrgChange={setSelectedOrg}
        onBack={() => setShowCertificates(false)}
      />
    );
  }

  if (editingCohort) {
    const workshopItems = localSchedule.map((item, idx) => ({ item, idx })).filter(({ item }) => item.type === 'workshop');
    const mentorshipItems = localSchedule.map((item, idx) => ({ item, idx })).filter(({ item }) => item.type === 'mentorship');
    // Las rutas de autoaprendizaje (cohortes perpetuas) no tienen sesiones en vivo,
    // Demo Day ni certificado: ocultamos esas pestañas para no confundir al equipo.
    const isPerpetualEdit = (editingCohort as any).is_perpetual === true;
    const hiddenPerpetualTabs = ['schedule', 'demoday', 'certification'];
    const activeEditTab = isPerpetualEdit && hiddenPerpetualTabs.includes(editTab) ? 'general' : editTab;

    return (
      <>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={closeEdit}><ArrowLeft className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-2xl font-bold">{editName}</h1>
                <p className="text-muted-foreground">
                  {isPerpetualEdit
                    ? `${editingCohort.program_name || '—'} · Ruta de autoaprendizaje (sin fechas)`
                    : `${editingCohort.program_name || '—'} · ${editStartDate ? format(editStartDate, 'dd MMM yyyy', { locale: es }) : '—'} - ${editEndDate ? format(editEndDate, 'dd MMM yyyy', { locale: es }) : '—'}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {isPerpetualEdit && (
                <Badge variant="secondary" className="gap-1">∞ Autoaprendizaje</Badge>
              )}
              {getStatusBadge(editStatus)}
              {isSuperAdmin && (
                <Button onClick={handleSaveEdit} disabled={cm.updateCohort.isPending}>
                  <Save className="w-4 h-4 mr-2" />Guardar
                </Button>
              )}
            </div>
          </div>

          {isPerpetualEdit && (
            <div className="p-3 rounded-lg border border-border/50 bg-muted/30 text-sm text-muted-foreground">
              Esta es una ruta de autoaprendizaje: no tiene sesiones en vivo, Demo Day ni certificado, por eso esas pestañas no aparecen.
            </div>
          )}

          <Tabs value={activeEditTab} onValueChange={setEditTab}>
            <TabsList className={`grid w-full ${isPerpetualEdit ? 'grid-cols-3' : 'grid-cols-6'}`}>
              <TabsTrigger value="general" className="gap-2"><Edit className="w-4 h-4" />General</TabsTrigger>
              {!isPerpetualEdit && (
                <TabsTrigger value="schedule" className="gap-2"><CalendarLucide className="w-4 h-4" />Calendario</TabsTrigger>
              )}
              <TabsTrigger value="progress" className="gap-2"><BarChart3 className="w-4 h-4" />Progreso</TabsTrigger>
              <TabsTrigger value="participants" className="gap-2"><Users className="w-4 h-4" />Participantes</TabsTrigger>
              {!isPerpetualEdit && (
                <>
                  <TabsTrigger value="demoday" className="gap-2"><Presentation className="w-4 h-4" />Demo Day</TabsTrigger>
                  <TabsTrigger value="certification" className="gap-2"><Award className="w-4 h-4" />Certificados</TabsTrigger>
                </>
              )}
            </TabsList>


            {/* General Tab */}
            <TabsContent value="general">
              {!isSuperAdmin && (
                <div className="mb-4 p-3 rounded-lg border border-border/50 bg-muted/30 text-sm text-muted-foreground">
                  Vista de solo lectura. Como Admin de Empresa puedes consultar la información pero no editarla.
                </div>
              )}
              <div className={cn("grid grid-cols-1 lg:grid-cols-2 gap-6", !isSuperAdmin && "pointer-events-none opacity-70 select-none")}>
                <Card>
                  <CardHeader><CardTitle>Datos de la Cohorte</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre</Label>
                      <Input value={editName} onChange={e => setEditName(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Ruta</Label>
                      <Select value={editProgramId} onValueChange={setEditProgramId}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {cm.programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Fecha Inicio</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editStartDate ? format(editStartDate, 'PPP', { locale: es }) : 'Seleccionar'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={editStartDate} defaultMonth={editStartDate} initialFocus onSelect={d => d && setEditStartDate(d)} className="p-3 pointer-events-auto" />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Fecha Fin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editEndDate ? format(editEndDate, 'PPP', { locale: es }) : 'Seleccionar'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={editEndDate} defaultMonth={editEndDate} initialFocus onSelect={d => d && setEditEndDate(d)} className="p-3 pointer-events-auto" />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    {/* Bloque "Calcular fechas vacías": aparece si la cohorte tiene
                        sesiones sin scheduled_date y hay fecha de inicio. Respeta
                        los items que ya tienen fecha asignada manualmente. */}
                    {editStartDate && localSchedule.some(it => !it.scheduled_date && (it.type === 'workshop' || it.type === 'mentorship')) && (
                      <div className="rounded-lg border border-violet-400/30 bg-violet-500/5 p-3 space-y-2">
                        <div className="flex items-start gap-2 text-xs text-violet-200/90">
                          <span aria-hidden className="text-base leading-none">📅</span>
                          <p>
                            Hay sesiones sin fecha en esta cohorte. Calcúlalas automáticamente desde la fecha de inicio (los items que ya tengan fecha no se modifican).
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Label className="text-xs shrink-0">Cada</Label>
                          <Input
                            type="number"
                            min={1}
                            max={30}
                            value={editRecalcPeriodicity}
                            onChange={e => setEditRecalcPeriodicity(Math.max(1, parseInt(e.target.value) || 7))}
                            className="w-16 text-center h-8"
                          />
                          <span className="text-xs text-muted-foreground">días entre semanas</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={cm.recalculateEmptyScheduleDates.isPending}
                            onClick={async () => {
                              if (!editingCohortId || !editStartDate) return;
                              try {
                                const res = await cm.recalculateEmptyScheduleDates.mutateAsync({
                                  cohort_id: editingCohortId,
                                  start_date: format(editStartDate, 'yyyy-MM-dd'),
                                  periodicity_days: editRecalcPeriodicity,
                                });
                                setScheduleInitialized(false);
                                toast({
                                  title: 'Fechas calculadas',
                                  description: res.updated > 0
                                    ? `${res.updated} sesión(es) actualizadas. Recuerda sincronizar con Calendar.`
                                    : 'No había sesiones vacías para calcular.',
                                });
                              } catch (err: any) {
                                toast({
                                  title: 'Error al calcular fechas',
                                  description: err?.message || 'Inténtalo de nuevo.',
                                  variant: 'destructive',
                                });
                              }
                            }}
                          >
                            Calcular fechas vacías
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Fecha límite entrega de proyecto</Label>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editProjectDeadline ? format(editProjectDeadline, 'PPP', { locale: es }) : 'Sin definir'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={editProjectDeadline} defaultMonth={editProjectDeadline} initialFocus onSelect={d => setEditProjectDeadline(d)} className="p-3 pointer-events-auto" />
                          </PopoverContent>
                        </Popover>
                        <Select value={editProjectDeadlineWeek} onValueChange={setEditProjectDeadlineWeek}>
                          <SelectTrigger className="w-[120px]"><SelectValue placeholder="Semana" /></SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 16 }, (_, i) => i + 1).map(w => (
                              <SelectItem key={w} value={String(w)}>Semana {w}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha límite laboratorio reto y soluciones</Label>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editChallengeLabDeadline ? format(editChallengeLabDeadline, 'PPP', { locale: es }) : 'Sin definir'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={editChallengeLabDeadline} defaultMonth={editChallengeLabDeadline} initialFocus onSelect={d => setEditChallengeLabDeadline(d)} className="p-3 pointer-events-auto" />
                          </PopoverContent>
                        </Popover>
                        <Select value={editChallengeLabDeadlineWeek} onValueChange={setEditChallengeLabDeadlineWeek}>
                          <SelectTrigger className="w-[120px]"><SelectValue placeholder="Semana" /></SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 16 }, (_, i) => i + 1).map(w => (
                              <SelectItem key={w} value={String(w)}>Semana {w}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha límite de acceso a contenido</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editContentAccessDeadline ? format(editContentAccessDeadline, 'PPP', { locale: es }) : 'Sin definir (ilimitado)'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={editContentAccessDeadline} defaultMonth={editContentAccessDeadline} initialFocus onSelect={d => setEditContentAccessDeadline(d)} className="p-3 pointer-events-auto" />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Borrador</SelectItem>
                          <SelectItem value="active">Activa</SelectItem>
                          <SelectItem value="completed">Completada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-6">
                  <Card>
                    <CardHeader><CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" />Precio e Inscripción</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Precio</Label>
                          <Input type="number" placeholder="0.00" value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Moneda</Label>
                          <Select value={editCurrency} onValueChange={setEditCurrency}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="MXN">MXN</SelectItem>
                              <SelectItem value="COP">COP</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="enrollment-switch">Inscripción abierta</Label>
                        <Switch id="enrollment-switch" checked={editEnrollmentOpen} onCheckedChange={setEditEnrollmentOpen} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle>Resumen</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Ruta</span>
                        <span className="font-medium">{editingCohort.program_name || '—'}</span>
                      </div>
                      <div className="space-y-2 pt-2 border-t border-border/40">
                        <Label className="text-xs text-muted-foreground">Organización</Label>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="edit-is-public"
                            checked={editIsPublic}
                            onCheckedChange={(v) => { setEditIsPublic(!!v); if (v) setEditOrgId(''); }}
                          />
                          <Label htmlFor="edit-is-public" className="text-sm font-normal cursor-pointer">
                            Cohorte pública (sin organización)
                          </Label>
                        </div>
                        {!editIsPublic && (
                          <OrganizationFilter
                            selectedOrg={editOrgId}
                            onOrgChange={setEditOrgId}
                            showAllOption={false}
                          />
                        )}
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Participantes</span>
                        <span className="font-medium">{editingCohort.participants_count}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Sesiones en calendario</span>
                        <span className="font-medium">{localSchedule.length}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <CohortResultsReportCard
                    cohortId={editingCohort.id}
                    isSuperAdmin={isSuperAdmin}
                    reportPath={editingCohort.results_report_url}
                    reportName={editingCohort.results_report_name}
                    uploadedAt={editingCohort.results_report_uploaded_at}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Schedule Tab */}
            <TabsContent value="schedule">
              {!isSuperAdmin && (
                <div className="mb-4 p-3 rounded-lg border border-border/50 bg-muted/30 text-sm text-muted-foreground">
                  Vista de solo lectura del calendario.
                </div>
              )}
              <div className={cn("space-y-6", !isSuperAdmin && "pointer-events-none opacity-70 select-none")}>
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium flex items-center gap-2"><CalendarLucide className="w-4 h-4 text-primary" /> Sincronización con Google Calendar y Fireflies</p>
                      <p className="text-xs text-muted-foreground mt-1">Crea los eventos con Meet en el calendario de los participantes (sin enviar correos) y trae las grabaciones al terminar la sesión.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="default" disabled={!editingCohortId || calSync.syncingCalendar}>
                            {calSync.syncingCalendar ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CalendarLucide className="w-4 h-4 mr-2" />}
                            Sincronizar todos con Calendar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Sincronizar con Google Calendar?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se sincronizarán únicamente las <strong>{upcomingSessionsCount}</strong> sesiones futuras de esta cohorte
                              (las {pastSessionsCount} sesiones ya pasadas se omiten). Los eventos aparecerán en el calendario de
                              aproximadamente <strong>{editingCohort?.participants_count ?? 0}</strong> participantes
                              <strong> sin enviar ningún correo</strong>.
                              {cohortFinishedForSync && ' Esta cohorte ya finalizó o no está activa, por lo que la sincronización será rechazada.'}
                            </AlertDialogDescription>

                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={async () => {
                              if (!editingCohortId) return;
                              if (!(await saveBeforeSync())) return;
                              calSync.syncCalendar(editingCohortId).then(refreshCalendarFields).catch(() => {});
                            }}>
                              Sincronizar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <Button size="sm" variant="outline" disabled={!editingCohortId || calSync.syncingRecordings} onClick={() => editingCohortId && calSync.syncRecordings(editingCohortId).then(refreshCalendarFields).catch(() => {})}>
                        {calSync.syncingRecordings ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Video className="w-4 h-4 mr-2" />}
                        Traer grabaciones
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" disabled={!editingCohortId || resyncingCurriculum}>
                            {resyncingCurriculum ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                            Actualizar currículo
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Actualizar el currículo de esta cohorte?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Reemplaza el contenido obligatorio congelado de esta cohorte con el estado <strong>actual</strong> de la ruta.
                              Úsalo solo si querés que los cambios recientes de contenido (videos agregados/quitados) apliquen a esta cohorte.
                              Puede cambiar quién figura como "completo" o "certificable".
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => editingCohortId && handleResyncCurriculum(editingCohortId)}>
                              Actualizar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex items-center gap-2 justify-end">
                  <p className="text-sm text-muted-foreground">Las sesiones en vivo se heredan de la ruta; las mentorías se crean en cada cohorte. Aquí asigna fecha, hora, experto y links.</p>
                </div>

                {scheduleQuery.isLoading ? (
                  <Card><CardContent className="p-8"><div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div></CardContent></Card>
                ) : localSchedule.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center space-y-4">
                      <p className="text-muted-foreground">No hay sesiones en el calendario de esta cohorte.</p>
                      <p className="text-sm text-muted-foreground">Las sesiones se generan automáticamente desde la definición de la ruta al crear la cohorte.</p>
                      {isSuperAdmin && programHasMentorships && (
                        <Button variant="outline" size="sm" onClick={addCohortMentorship} className="gap-2 mx-auto">
                          <Plus className="w-4 h-4" />
                          Agregar sesión de mentoría
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (

                  <>
                    {workshopItems.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Presentation className="w-5 h-5 text-primary" />
                            Sesiones en Vivo ({workshopItems.length})
                          </CardTitle>
                          <CardDescription>Define fecha, hora, experto y link de conexión para cada sesión en vivo</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {workshopItems.map(({ item, idx }) => renderScheduleCard(item, idx, 'workshop'))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {(mentorshipItems.length > 0 || (isSuperAdmin && programHasMentorships)) && (
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between gap-2">
                          <div>
                            <CardTitle className="flex items-center gap-2">
                              <GraduationCap className="w-5 h-5 text-secondary" />
                              Mentorías ({mentorshipItems.length})
                            </CardTitle>
                            <CardDescription>
                              Las mentorías son propias de esta cohorte: crearlas, editarlas o eliminarlas no afecta a la ruta ni a las demás cohortes.
                            </CardDescription>
                          </div>
                          {isSuperAdmin && programHasMentorships && (
                            <Button variant="outline" size="sm" onClick={addCohortMentorship} className="gap-2 shrink-0">
                              <Plus className="w-4 h-4" />
                              Agregar sesión de mentoría
                            </Button>
                          )}
                        </CardHeader>
                        <CardContent>
                          {mentorshipItems.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No hay mentorías en esta cohorte. Crea la primera con el botón superior.
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {mentorshipItems.map(({ item, idx }) => renderScheduleCard(item, idx, 'mentorship'))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </TabsContent>

            {/* Participants Tab */}
            <TabsContent value="participants">
              <div className="space-y-6">
                <Card>
                   <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Participantes ({participantsQuery.data?.length || 0})</CardTitle>
                      <CardDescription>Gestiona los participantes asignados a esta cohorte</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {(participantsQuery.data?.length || 0) > 0 && (
                        <Button variant="outline" size="sm" onClick={() => {
                          const data = (participantsQuery.data || []).map(p => ({
                            name: p.full_name,
                            email: p.email,
                            area: p.area || '',
                            enrolled: p.enrolled_at ? format(new Date(p.enrolled_at), 'dd/MM/yyyy') : '',
                          }));
                          exportToExcel(data, [
                            { key: 'name', header: 'Nombre' },
                            { key: 'email', header: 'Email' },
                            { key: 'area', header: 'Área' },
                            { key: 'enrolled', header: 'Fecha inscripción' },
                          ], { fileName: `participantes-${editingCohort?.name || 'cohorte'}`, title: 'Participantes' });
                          toast({ title: 'Exportado', description: 'Lista de participantes descargada.' });
                        }}>
                          <Download className="w-4 h-4 mr-2" />Exportar
                        </Button>
                      )}
                      {isSuperAdmin && (
                        <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
                          <Upload className="w-4 h-4 mr-2" />Importar
                        </Button>
                      )}
                      {!isAuditor && (
                        <Button onClick={() => editingCohort && handleOpenAssignDialog(editingCohort)}>
                          <UserPlus className="w-4 h-4 mr-2" />Gestionar Participantes
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {participantsQuery.isLoading ? (
                      <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
                    ) : (participantsQuery.data?.length || 0) === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No hay participantes asignados.</p>
                    ) : (
                      <div className="space-y-2">
                        {participantsQuery.data?.map(p => (
                          <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-border/50">
                            <div className="flex-1">
                              <p className="font-medium">{p.full_name}</p>
                              <p className="text-sm text-muted-foreground">{p.email}{p.area ? ` · ${p.area}` : ''}</p>
                            </div>
                            {p.enrolled_at && (
                              <span className="text-xs text-muted-foreground">
                                Inscrito {format(new Date(p.enrolled_at), 'dd MMM yyyy', { locale: es })}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Mentors Section */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-secondary" />
                        Mentores ({mentorsQuery.data?.length || 0})
                      </CardTitle>
                      <CardDescription>Mentores asignados a esta cohorte</CardDescription>
                    </div>
                    {cm.isSuperAdmin && (
                      <Button onClick={() => editingCohort && handleOpenMentorDialog(editingCohort)}>
                        <ShieldCheck className="w-4 h-4 mr-2" />Gestionar Mentores
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {mentorsQuery.isLoading ? (
                      <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
                    ) : (mentorsQuery.data?.length || 0) === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No hay mentores asignados.</p>
                    ) : (
                      <div className="space-y-2">
                        {mentorsQuery.data?.map(m => (
                          <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border border-border/50">
                            <div className="p-2 rounded-full bg-secondary/10">
                              <ShieldCheck className="w-4 h-4 text-secondary" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{m.full_name}</p>
                              <p className="text-sm text-muted-foreground">{m.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Auditors Section (solo lectura, asignación por super_admin) */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5 text-primary" />
                        Auditores ({auditorsQuery.data?.length || 0})
                      </CardTitle>
                      <CardDescription>Auditores (solo lectura) asignados a esta cohorte</CardDescription>
                    </div>
                    {cm.isSuperAdmin && (
                      <Button onClick={() => editingCohort && handleOpenAuditorDialog(editingCohort)}>
                        <Eye className="w-4 h-4 mr-2" />Gestionar Auditores
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {auditorsQuery.isLoading ? (
                      <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
                    ) : (auditorsQuery.data?.length || 0) === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No hay auditores asignados.</p>
                    ) : (
                      <div className="space-y-2">
                        {auditorsQuery.data?.map(a => (
                          <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-border/50">
                            <div className="p-2 rounded-full bg-primary/10">
                              <Eye className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{a.full_name}</p>
                              <p className="text-sm text-muted-foreground">{a.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

              </div>
            </TabsContent>

            {/* Demo Day Tab */}
            <TabsContent value="demoday">
              {!isSuperAdmin && (
                <div className="mb-4 p-3 rounded-lg border border-border/50 bg-muted/30 text-sm text-muted-foreground">
                  Vista de solo lectura del Demo Day.
                </div>
              )}
              <Card className={cn(!isSuperAdmin && "pointer-events-none opacity-70 select-none")}>
                <CardHeader>
                  <CardTitle>Configuración Demo Day</CardTitle>
                  <CardDescription>Define si esta cohorte tiene Demo Day y su configuración</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="demoday-switch" className="font-medium">Habilitar Demo Day</Label>
                      <p className="text-sm text-muted-foreground">Activa o desactiva el Demo Day para esta cohorte</p>
                    </div>
                    <Switch id="demoday-switch" checked={editDemoDayEnabled} onCheckedChange={setEditDemoDayEnabled} />
                  </div>
                  {editDemoDayEnabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Fecha Demo Day</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editDemoDayDate ? format(editDemoDayDate, 'PPP', { locale: es }) : 'Seleccionar'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={editDemoDayDate} defaultMonth={editDemoDayDate} initialFocus onSelect={d => d && setEditDemoDayDate(d)} className="p-3 pointer-events-auto" />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Hora Inicio</Label>
                        <Select value={editDemoDayStartHour.toString()} onValueChange={v => setEditDemoDayStartHour(parseInt(v))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{hours.map(h => <SelectItem key={h.value} value={h.value.toString()}>{h.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Hora Fin</Label>
                        <Select value={editDemoDayEndHour.toString()} onValueChange={v => setEditDemoDayEndHour(parseInt(v))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{hours.map(h => <SelectItem key={h.value} value={h.value.toString()}>{h.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Link Demo Day</Label>
                        <Input value={editDemoDayLink} onChange={e => setEditDemoDayLink(e.target.value)} placeholder="https://meet.google.com/..." />
                      </div>
                    </div>
                  )}
                  {editDemoDayEnabled && isSuperAdmin && (
                    <div className="pt-4 border-t flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <p className="text-sm font-medium">Sincronizar Demo Day con Google Calendar</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Crea el evento del Demo Day en el calendario de los participantes, sin enviar correos (igual que las sesiones). No toca las sesiones. Guardá los cambios antes de sincronizar.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!editingCohortId || !editDemoDayDate || calSync.syncingCalendar}
                        onClick={async () => {
                          if (!editingCohortId) return;
                          if (!(await saveBeforeSync())) return;
                          calSync.syncCalendar(editingCohortId, false, true).then(refreshDemoDayMeetLink).catch(() => {});
                        }}
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {calSync.syncingCalendar ? 'Sincronizando…' : 'Sincronizar Demo Day'}
                      </Button>
                    </div>
                  )}
                  {editDemoDayEnabled && editDemoDayMeetLink && (
                    <div className="space-y-2">
                      <Label>Meet generado (solo lectura)</Label>
                      <div className="flex items-center gap-2">
                        <Input value={editDemoDayMeetLink} readOnly className="text-muted-foreground" />
                        <a href={editDemoDayMeetLink} target="_blank" rel="noreferrer">
                          <Button type="button" variant="outline" size="icon">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Se generó automáticamente al sincronizar (no hay link manual en "Link Demo Day"). No editable acá.
                      </p>
                    </div>
                  )}
                  {editDemoDayEnabled && (
                    <div className="mt-4 space-y-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label>Link de grabación del Demo Day</Label>
                        <Input
                          value={editDemoDayRecordingLink}
                          onChange={e => setEditDemoDayRecordingLink(e.target.value)}
                          placeholder="https://vimeo.com/... o https://drive.google.com/..."
                        />
                        <p className="text-xs text-muted-foreground">URL de la grabación final del Demo Day (Vimeo, Drive, YouTube, etc.).</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Transcripción del Demo Day</Label>
                        {editDemoDayTranscriptUrl ? (
                          <div className="flex items-center gap-2 rounded-md border border-border p-3 bg-muted/30">
                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="text-sm truncate flex-1">{editDemoDayTranscriptName || 'Transcripción.pdf'}</span>
                            <Button type="button" variant="ghost" size="sm" onClick={() => window.open(editDemoDayTranscriptUrl, '_blank')}>
                              <ExternalLink className="h-3 w-3 mr-1" />Ver
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => { setEditDemoDayTranscriptUrl(''); setEditDemoDayTranscriptName(''); }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={uploadingTranscript}
                              onClick={() => transcriptInputRef.current?.click()}
                            >
                              {uploadingTranscript
                                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Subiendo...</>
                                : <><Upload className="mr-2 h-4 w-4" />Subir transcripción</>}
                            </Button>
                            <span className="text-xs text-muted-foreground">PDF, TXT, DOCX, VTT, SRT o MD</span>
                          </div>
                        )}
                        <input
                          ref={transcriptInputRef}
                          type="file"
                          accept=".pdf,.txt,.docx,.vtt,.srt,.md,application/pdf,text/plain"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !editingCohortId) return;
                            setUploadingTranscript(true);
                            try {
                              const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                              const path = `demoday/${editingCohortId}/${Date.now()}_${safeName}`;
                              const { error: upErr } = await supabase.storage.from('content').upload(path, file, {
                                cacheControl: '3600',
                                upsert: false,
                              });
                              if (upErr) throw upErr;
                              const { data: urlData } = supabase.storage.from('content').getPublicUrl(path);
                              setEditDemoDayTranscriptUrl(urlData.publicUrl);
                              setEditDemoDayTranscriptName(file.name);
                              toast({ title: 'Transcripción subida', description: 'Recuerda guardar los cambios.' });
                            } catch (err: any) {
                              toast({ title: 'Error al subir', description: err.message || '', variant: 'destructive' });
                            } finally {
                              setUploadingTranscript(false);
                              if (transcriptInputRef.current) transcriptInputRef.current.value = '';
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Progress Tab */}
            <TabsContent value="progress">
              <CohortProgressTab
                cohortId={editingCohortId!}
                participants={participantsQuery.data || []}
                participantsLoading={participantsQuery.isLoading}
                scheduleItems={localSchedule}
                cohortStartDate={weekAnchorDate(editingCohort?.start_date, (editingCohort as any)?.first_session_after_start)}
                cohortName={editingCohort?.name ?? null}
              />
            </TabsContent>

            {/* Certification Tab */}
            <TabsContent value="certification">
              <CohortCertificationTab
                cohortId={editingCohortId!}
                programId={editProgramId}
                organizationId={editingCohort?.organization_id ?? null}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      {sharedDialogs}
    </>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Gestión de Cohortes</h1>
            <p className="text-muted-foreground mt-1">Configura y administra las cohortes de certificación</p>
          </div>
          <div className="flex items-center gap-3">
            {(cm.isSuperAdmin || isAuditor) && (
              <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
            )}
            {isSuperAdmin && (
              <Button variant="outline" className="gap-2" onClick={() => setShowCertificates(true)}>
                <Award className="w-4 h-4" />Certificados Generados
              </Button>
            )}
            {isSuperAdmin && (
              <Button className="gap-2" onClick={openCreateDialog}>
                <Plus className="w-4 h-4" />Nueva Cohorte
              </Button>
            )}
            {!isSuperAdmin && !isAuditor && (
              <Button className="gap-2" onClick={() => setIsRequestDialogOpen(true)}>
                <Send className="w-4 h-4" />Solicitar contratación de cohorte
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20"><CalendarLucide className="w-5 h-5 text-primary" /></div>
                <div><p className="text-2xl font-bold">{cm.filteredCohorts.length}</p><p className="text-sm text-muted-foreground">Total Cohortes</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20"><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
                <div><p className="text-2xl font-bold">{cm.filteredCohorts.filter(c => c.status === 'active').length}</p><p className="text-sm text-muted-foreground">Activas</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary/20"><Users className="w-5 h-5 text-secondary" /></div>
                <div><p className="text-2xl font-bold">{cm.filteredCohorts.reduce((acc, c) => acc + c.participants_count, 0)}</p><p className="text-sm text-muted-foreground">Participantes</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-accent/20"><Presentation className="w-5 h-5 text-accent" /></div>
                <div><p className="text-2xl font-bold">{cm.filteredCohorts.filter(c => c.status === 'completed').length}</p><p className="text-sm text-muted-foreground">Completadas</p></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Buscar por nombre, ruta u organización..." value={searchText} onChange={e => setSearchText(e.target.value)} className="pl-10" />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="active">Activa</SelectItem>
                  <SelectItem value="completed">Completada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap gap-3 mt-3">
              <Select value={filterModality} onValueChange={setFilterModality}>
                <SelectTrigger className="w-[190px]"><SelectValue placeholder="Tipo de ruta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos de ruta</SelectItem>
                  <SelectItem value="cohort">Acompañamiento</SelectItem>
                  <SelectItem value="self_paced">Autoaprendizaje</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterProgram} onValueChange={setFilterProgram}>
                <SelectTrigger className="w-[220px]"><SelectValue placeholder="Ruta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las rutas</SelectItem>
                  {programOptions.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {categoryOptions.length > 0 && (
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[190px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {categoryOptions.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={filterVisibility} onValueChange={setFilterVisibility}>
                <SelectTrigger className="w-[170px]"><SelectValue placeholder="Visibilidad" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Pública y empresa</SelectItem>
                  <SelectItem value="public">Pública</SelectItem>
                  <SelectItem value="private">De empresa</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterEnrollment} onValueChange={setFilterEnrollment}>
                <SelectTrigger className="w-[180px]"><SelectValue placeholder="Inscripción" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toda inscripción</SelectItem>
                  <SelectItem value="open">Inscripción abierta</SelectItem>
                  <SelectItem value="closed">Inscripción cerrada</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterTimeframe} onValueChange={setFilterTimeframe}>
                <SelectTrigger className="w-[170px]"><SelectValue placeholder="Vigencia" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Cualquier fecha</SelectItem>
                  <SelectItem value="ongoing">En curso hoy</SelectItem>
                  <SelectItem value="upcoming">Próximas</SelectItem>
                  <SelectItem value="finished">Finalizadas</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters}>Limpiar filtros</Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cohorts Table */}
        <Card>
          <CardHeader>
            <CardTitle>Cohortes</CardTitle>
            <CardDescription>
              Mostrando {displayCohorts.length} de {cm.filteredCohorts.length} cohortes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {cm.isLoading ? (
              <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
            ) : displayCohorts.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No hay cohortes que coincidan.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Organización</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Demoday</TableHead>
                    <TableHead>Participantes</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayCohorts.map(cohort => {
                    const participantQuotaWarning = quotaWarning(cohort);
                    return (
                    <TableRow key={cohort.id} className="cursor-pointer" onClick={() => openEditView(cohort)}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {cohort.name}
                          {(cohort as any).is_perpetual && (
                            <Badge variant="secondary">∞ Autoaprendizaje</Badge>
                          )}
                          {isSuperAdmin && isPastDate(cohort.end_date) && !cohort.results_report_url && (
                            <WarnIcon text="La cohorte ya terminó y no tiene informe de resultados cargado." />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          {cohort.program_name || '—'}
                          {isSuperAdmin && cohort.sessions_missing_recording > 0 && (
                            <WarnIcon
                              text={
                                `${cohort.sessions_missing_recording} sesión(es) en vivo ya realizadas sin grabación:\n` +
                                cohort.missing_recording_samples
                                  .map(s => `• ${s.date ? format(parseLocalDate(s.date)!, 'dd MMM yyyy', { locale: es }) : 'Sin fecha'} — ${s.title}`)
                                  .join('\n')
                              }
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          {cohort.organization_name || (cohort.is_public ? 'Pública' : '—')}
                          {isSuperAdmin && cohort.organization_id && completedEnterpriseDiagnostics.isSuccess &&
                            !completedEnterpriseDiagnostics.data.has(cohort.organization_id) && (
                              <WarnIcon text="Esta empresa aún no ha completado el diagnóstico empresarial" />
                            )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {cohort.start_date && <p>{format(parseLocalDate(cohort.start_date)!, 'dd MMM yyyy', { locale: es })}</p>}
                          {cohort.end_date && <p className="text-muted-foreground">- {format(parseLocalDate(cohort.end_date)!, 'dd MMM yyyy', { locale: es })}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          {cohort.demo_day_date
                            ? format(parseLocalDate(cohort.demo_day_date)!, 'dd MMM yyyy', { locale: es })
                            : <span className="text-muted-foreground">—</span>}
                          {isSuperAdmin && demoDayMissing(cohort) && (
                            <WarnIcon text={`Demoday realizado: ${demoDayMissing(cohort)}.`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="gap-1.5 whitespace-nowrap">
                            {cohort.participants_count}
                            {participantQuotaWarning && <WarnIcon text={participantQuotaWarning} />}
                          </Badge>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {cohort.certified_count} certificados
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {getStatusBadge(cohort.status)}
                          {getWeekLabel(cohort) && (
                            <p className="text-xs text-muted-foreground">{getWeekLabel(cohort)}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditView(cohort)} title={isSuperAdmin ? 'Editar' : 'Ver'}>
                            {isSuperAdmin ? <Edit className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          {isSuperAdmin && (
                            <Button variant="ghost" size="icon" onClick={async () => {
                              try {
                                await cm.duplicateCohort.mutateAsync(cohort.id);
                                toast({ title: 'Cohorte duplicada', description: `"${cohort.name}" copiada como borrador.` });
                              } catch (err: any) {
                                toast({ title: 'Error al duplicar', description: err.message, variant: 'destructive' });
                              }
                            }} title="Duplicar">
                              <Copy className="w-4 h-4" />
                            </Button>
                          )}
                          {isSuperAdmin && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Eliminar cohorte?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Se eliminará "{cohort.name}" junto con su calendario, participantes y mentores. Esta acción no se puede deshacer.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteCohort(cohort.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Crear Nueva Cohorte</DialogTitle>
              <DialogDescription>Configura los datos básicos. El calendario se generará automáticamente desde las sesiones en vivo de la ruta.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input placeholder="Ej: Cohorte Q3 2026" value={formName} onChange={e => setFormName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Ruta</Label>
                <Select value={formProgramId} onValueChange={setFormProgramId}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar ruta" /></SelectTrigger>
                  <SelectContent>{cm.programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {cm.isSuperAdmin && (
                <>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={formIsPublic} onCheckedChange={(v) => setFormIsPublic(!!v)} id="is_public" />
                    <Label htmlFor="is_public">Cohorte pública (sin organización)</Label>
                  </div>
                  {!formIsPublic && (
                    <div className="space-y-2">
                      <Label>Organización</Label>
                      <OrganizationFilter selectedOrg={formOrgId} onOrgChange={setFormOrgId} showAllOption={false} />
                    </div>
                  )}
                </>
              )}
              {/* Fechas + Periodicidad — labels de igual altura para alinear los pickers */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="h-5 flex items-center">
                    <Label>Fecha de Inicio{isLeaderProfessionalProgram ? '' : ' *'}</Label>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formStartDate ? format(formStartDate, 'PPP', { locale: es }) : 'Seleccionar'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={formStartDate} onSelect={d => setFormStartDate(d)} className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <div className="h-5 flex items-center justify-between">
                    <Label>Fecha de Fin</Label>
                    {formEndDateCalculated && formEndDate?.getTime() === formEndDateCalculated.getTime() && (
                      <span className="text-[10px] text-muted-foreground">calculada automáticamente</span>
                    )}
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formEndDate ? format(formEndDate, 'PPP', { locale: es }) : 'Seleccionar'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={formEndDate} onSelect={d => setFormEndDate(d)} className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Periodicidad global */}
              <div className="flex items-center gap-3">
                <Label className="shrink-0">Cada</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={formSessionPeriodicity}
                  onChange={e => setFormSessionPeriodicity(Math.max(1, parseInt(e.target.value) || 7))}
                  className="w-16 text-center"
                />
                <span className="text-sm text-muted-foreground">días entre semanas</span>
              </div>

              {/* Banner suave: en Profesional Líder IA la fecha de inicio es opcional */}
              {formProgramId && isLeaderProfessionalProgram && !formStartDate && (
                <div className="rounded-lg border border-violet-400/30 bg-violet-500/5 p-3 text-xs text-violet-200/90 flex items-start gap-2">
                  <span aria-hidden className="text-base leading-none">💡</span>
                  <div className="space-y-1">
                    <p className="font-medium text-violet-300">Fecha de inicio opcional para Profesional Líder IA.</p>
                    <p>
                      Puedes crear la cohorte sin fecha y asignarla más tarde desde edición; al hacerlo, podrás recalcular las fechas vacías de workshops y mentorías con un clic.
                    </p>
                  </div>
                </div>
              )}

              {/* Sugerencia de fechas con detalle de sesiones + edición inline.
                  Para Profesional Líder IA sin fecha de inicio, se omite y se muestra solo el banner. */}
              {formProgramId && !(isLeaderProfessionalProgram && !formStartDate) && (
                <div className="space-y-4">
                  {/* Sesiones en vivo */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Sesiones en vivo <span className="text-muted-foreground font-normal">(editables por semana)</span></Label>
                      {programSessionsQuery.data && (
                        <span className="text-xs text-muted-foreground">
                          {programSessionsQuery.data.workshopsCount} en vivo
                        </span>
                      )}
                    </div>

                    {programSessionsQuery.isLoading ? (
                      <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-sm text-muted-foreground">Cargando sesiones de la ruta...</div>
                    ) : programSessionsQuery.isError ? (
                      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                        {(programSessionsQuery.error as Error)?.message || 'No se pudieron cargar las sesiones de la ruta.'}
                      </div>
                    ) : !programSessionsQuery.data || programSessionsQuery.data.uniqueWeeks.length === 0 ? (
                      <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-sm text-muted-foreground">La ruta seleccionada no tiene sesiones con número de semana asignado.</div>
                    ) : (() => {
                      const weeksWithWorkshops = programSessionsQuery.data.uniqueWeeks.filter(w =>
                        (programSessionsQuery.data!.sessionsByWeek[w] || []).some(s => s.type === 'workshop')
                      );
                      if (weeksWithWorkshops.length === 0) {
                        return <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-sm text-muted-foreground">La ruta no tiene sesiones en vivo programadas.</div>;
                      }
                      return (
                        <div className="rounded-lg border border-border/50 divide-y divide-border/30 overflow-hidden max-h-56 overflow-y-auto">
                          {weeksWithWorkshops.map(weekNum => {
                            const overrideEntry = formWeekDateOverrides[weekNum];
                            const isCleared = weekNum in formWeekDateOverrides && overrideEntry === null;
                            const hasOverride = overrideEntry instanceof Date;
                            const calcDate = formStartDate ? (() => {
                              const dd = new Date(formStartDate);
                              dd.setDate(dd.getDate() + weekNum * formSessionPeriodicity);
                              return dd;
                            })() : null;
                            const displayDate: Date | null = isCleared ? null : ((overrideEntry as Date | undefined) ?? calcDate);
                            const workshops = (programSessionsQuery.data!.sessionsByWeek[weekNum] || []).filter(s => s.type === 'workshop');

                            return (
                              <div key={weekNum} className="flex items-start gap-3 px-3 py-2 bg-card hover:bg-muted/20 transition-colors">
                                <Badge
                                  variant={hasOverride ? 'default' : 'outline'}
                                  className="text-xs shrink-0 mt-0.5 tabular-nums"
                                >
                                  Sem. {weekNum}
                                </Badge>

                                <div className="flex-1 min-w-0 space-y-1">
                                  {workshops.map((s, i) => (
                                    <div key={i} className="flex items-center gap-1.5 text-xs">
                                      <span className="text-primary">🎙️</span>
                                      <span className="truncate text-foreground">{s.title}</span>
                                    </div>
                                  ))}
                                </div>

                                <div className="flex items-center gap-1 shrink-0">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        className={`text-xs px-2 py-1 rounded border transition-colors hover:bg-muted ${
                                          hasOverride
                                            ? 'border-primary/50 text-primary bg-primary/5'
                                            : 'border-border/50 text-muted-foreground'
                                        }`}
                                      >
                                        {displayDate
                                          ? format(displayDate, "d MMM yyyy", { locale: es })
                                          : 'Sin fecha'}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="end">
                                      <Calendar
                                        mode="single"
                                        selected={displayDate ?? undefined}
                                        onSelect={d => {
                                          if (d) {
                                            setFormWeekDateOverrides(prev => ({ ...prev, [weekNum]: d }));
                                          } else {
                                            setFormWeekDateOverrides(prev => {
                                              const next = { ...prev };
                                              delete next[weekNum];
                                              return next;
                                            });
                                          }
                                        }}
                                        className="p-3 pointer-events-auto"
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  {displayDate && (
                                    <button
                                      type="button"
                                      title="Quitar fecha"
                                      onClick={() => setFormWeekDateOverrides(prev => ({ ...prev, [weekNum]: null }))}
                                      className="text-muted-foreground hover:text-destructive transition-colors px-1"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                  {(hasOverride || isCleared) && (
                                    <button
                                      type="button"
                                      title="Restaurar fecha calculada"
                                      onClick={() => setFormWeekDateOverrides(prev => {
                                        const next = { ...prev };
                                        delete next[weekNum];
                                        return next;
                                      })}
                                      className="text-[10px] text-muted-foreground hover:text-destructive transition-colors px-1"
                                    >
                                      ↺
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {formEndDateCalculated && formEndDate && formEndDate.getTime() !== formEndDateCalculated.getTime() && (
                    <p className="text-xs text-amber-500/80 flex items-center gap-1.5">
                      <CalendarIcon className="w-3 h-3 shrink-0" />
                      Fecha de fin editada · sugerida:{' '}
                      <strong>{format(formEndDateCalculated, "d 'de' MMMM", { locale: es })}</strong>
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Fecha Demo Day (opcional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formDemoDayDate ? format(formDemoDayDate, 'PPP', { locale: es }) : 'Sin definir'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={formDemoDayDate} onSelect={d => setFormDemoDayDate(d)} className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateCohort} disabled={cm.createCohort.isPending}>Crear Cohorte</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {sharedDialogs}

        {/* Import participants dialog */}
        <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Importar Participantes</DialogTitle>
              <DialogDescription>
                Sube un archivo Excel o CSV con una columna de correos electrónicos. Los usuarios deben existir previamente en la plataforma.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                disabled={importLoading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImportFile(file);
                }}
              />
              {importLoading && <p className="text-sm text-muted-foreground">Procesando archivo...</p>}
            </div>
          </DialogContent>
        </Dialog>

        {/* Request cohort contract dialog (admin de empresa) */}
        <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Solicitar contratación de cohorte</DialogTitle>
              <DialogDescription>
                Tu solicitud será enviada a los administradores de LIDER IA para coordinar la contratación.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Ruta</Label>
                <Select value={requestProgramId} onValueChange={setRequestProgramId}>
                  <SelectTrigger><SelectValue placeholder="Selecciona una ruta" /></SelectTrigger>
                  <SelectContent>
                    {(cm.programs || [])
                      .filter((p: any) => p.status === 'public' && (p.modality ?? 'cohort') === 'cohort')
                      .map((p: any) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Fecha de inicio</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {requestStartDate ? format(requestStartDate, 'PP', { locale: es }) : 'Selecciona'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={requestStartDate} onSelect={setRequestStartDate} initialFocus className={cn('p-3 pointer-events-auto')} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Fecha de fin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {requestEndDate ? format(requestEndDate, 'PP', { locale: es }) : 'Selecciona'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={requestEndDate} onSelect={setRequestEndDate} initialFocus className={cn('p-3 pointer-events-auto')} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Input value={requestNotes} onChange={(e) => setRequestNotes(e.target.value)} placeholder="Información adicional" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)} disabled={requestSubmitting}>Cancelar</Button>
              <Button onClick={handleSubmitRequest} disabled={requestSubmitting} className="gap-2">
                <Send className="w-4 h-4" />
                {requestSubmitting ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmación de sincronización tras guardar (antes se hacía en silencio) */}
        <AlertDialog open={!!pendingSync} onOpenChange={(open) => { if (!open) setPendingSync(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Sincronizar con Google Calendar?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingSync?.reason} Se sincronizarán <strong>{pendingSync?.upcoming ?? 0}</strong> sesiones futuras
                (las pasadas se omiten) en el calendario de aproximadamente{' '}
                <strong>{pendingSync?.participants ?? 0}</strong> participantes.
                La sincronización <strong>no envía correos</strong>: los eventos se crean y actualizan en silencio.

              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>No sincronizar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const id = pendingSync?.cohortId;
                  setPendingSync(null);
                  if (id) calSync.syncCalendar(id).then(refreshCalendarFields).catch(() => {});
                }}
              >
                Sincronizar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>



      </div>
    </div>
  );
}
